import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Buildings, Upload, Check, CurrencyEur, ListBullets, CaretDown, CaretRight, Plus, Trash, Percent, Copy, Download,
  WhatsappLogo, PencilSimple, ChatCircleDots, FileText, CalendarCheck, Megaphone, Truck, Info,
  EnvelopeSimple, PaperPlaneTilt, Eye, EyeSlash, CircleNotch, CheckCircle, Bell, CreditCard, Crown, Lock, Sparkle, ShieldCheck, Warning, Users, Palette
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { productCategories, profileTypes } from '../config/productConfig';
import { ProductIcon } from '../components/WindowIcons';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useTranslation } from 'react-i18next';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useSubscription } from '../hooks/useSubscription';

import { API } from '../config/api';
import { ACCOUNTING_LAUNCHED, WHATSAPP_ALERTS_LAUNCHED, isPlanOffered } from '../config/features';
import ClientFollowupsSettings from '../components/ClientFollowupsSettings';
import AdminUsersTab from '../components/AdminUsersTab';
import ProductOptionsSettings from '../components/ProductOptionsSettings';

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [priceList, setPriceList] = useState({});
  const [customProfiles, setCustomProfiles] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isAddProfileOpen, setIsAddProfileOpen] = useState(false);
  const [newProfile, setNewProfile] = useState({ name: '', parent_profile_id: 'pvc', percentage_markup: 10, description: '', pricing: 'own' });
  const [formData, setFormData] = useState({
    company_name: user?.company_name || '',
    company_address: user?.company_address || '',
    company_phone: user?.company_phone || '',
    company_email: user?.company_email || '',
    company_vat: user?.company_vat || '',
    terms_conditions: user?.terms_conditions || '',
    default_quote_notes: user?.default_quote_notes || ''
  });

  // WhatsApp Templates state
  const [waTemplates, setWaTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', category: 'custom', body: '', icon: 'ChatCircleDots' });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({ resend_api_key: '', sender_email: '', sender_name: '', use_own_domain: false });
  const [emailSettingsLoaded, setEmailSettingsLoaded] = useState({ resend_api_key_masked: '', resend_api_key_set: false, sender_email: '', sender_name: '' });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showEmailAdvanced, setShowEmailAdvanced] = useState(false);

  // Quote template settings state
  const [quoteTemplate, setQuoteTemplate] = useState({
    primary_color: '#0033A0',
    show_logo: true,
    show_product_details: true,
    show_product_icons: true,
    show_iva_column: true,
    show_terms: true,
    show_signature: true,
    show_notes: true,
    show_validity: true,
    header_title: 'PREVENTIVO N.',
    client_section_title: 'DESTINATARIO',
    products_section_title: 'DETTAGLIO PRODOTTI',
    summary_section_title: 'RIEPILOGO ECONOMICO',
    notes_section_title: 'NOTE',
    terms_section_title: 'TERMINI E CONDIZIONI',
    signature_left: "Timbro e firma dell'azienda",
    signature_right: 'Firma del cliente per accettazione',
    footer_text: '',
    font_size: 'normal'
  });
  const [savingTemplate2, setSavingTemplate2] = useState(false);

  // WhatsApp Notifications state
  const [waNotifConfig, setWaNotifConfig] = useState({
    enabled: false, phone_number: '', notify_overdue_invoices: true,
    notify_quote_followup: true, notify_reminders: true, followup_days: 3
  });
  const [waNotifLoaded, setWaNotifLoaded] = useState(false);
  const [savingWaNotif, setSavingWaNotif] = useState(false);
  const [testingWa, setTestingWa] = useState(false);

  // SDI (OpenAPI.it) settings state
  const [sdiConfig, setSdiConfig] = useState({
    oauth_token: '',
    fiscal_id: '',
    company_name: '',
    company_address: '',
    company_cap: '',
    company_city: '',
    company_province: '',
    company_pec: '',
    regime_fiscale: 'RF01',
    business_registered: false
  });
  const [sdiLoaded, setSdiLoaded] = useState(false);
  const [savingSDI, setSavingSDI] = useState(false);
  const [registeringBusiness, setRegisteringBusiness] = useState(false);

  // Extra Products Library
  const [extraLibrary, setExtraLibrary] = useState([]);
  const [savingExtraLibrary, setSavingExtraLibrary] = useState(false);

  // AI Vocabulary (per-user custom codes)
  const [aiVocabulary, setAiVocabulary] = useState([]);
  const [savingAiVocabulary, setSavingAiVocabulary] = useState(false);
  const [aiStats, setAiStats] = useState(null);

  // Account name editing
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name) { toast.error('Il nome non può essere vuoto'); return; }
    setSavingName(true);
    try {
      const { data } = await axios.patch(`${API}/auth/me`, { name });
      updateUser({ name: data.name });
      setEditingName(false);
      toast.success('Nome aggiornato');
    } catch (err) {
      toast.error('Errore nel salvataggio del nome');
    } finally {
      setSavingName(false);
    }
  };
  const [aiLessons, setAiLessons] = useState([]);

  // Data Health
  const [healthData, setHealthData] = useState(null);
  const [normalizing, setNormalizing] = useState(false);

  const loadHealth = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/data-health`);
      setHealthData(data);
    } catch {}
  };

  const runNormalize = async () => {
    setNormalizing(true);
    try {
      const { data } = await axios.post(`${API}/settings/data-health/normalize`);
      const total = Object.values(data.fixed || {}).reduce((a,b) => a+b, 0);
      if (total > 0) {
        toast.success(`${total} documenti normalizzati`);
      } else {
        toast.info('Nessun documento da normalizzare');
      }
      await loadHealth();
    } catch {
      toast.error('Errore normalizzazione');
    } finally {
      setNormalizing(false);
    }
  };

  const reloadAiLessons = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/ai-lessons`);
      setAiLessons(data.lessons || []);
    } catch {}
  };

  const deleteLesson = async (id) => {
    try {
      await axios.delete(`${API}/settings/ai-lessons/${id}`);
      setAiLessons(aiLessons.filter(l => l.id !== id));
      toast.success('Correzione eliminata');
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const saveAiVocabulary = async () => {
    setSavingAiVocabulary(true);
    try {
      const clean = (aiVocabulary || [])
        .filter(v => v.code && v.code.trim() && v.name && v.name.trim())
        .map(v => ({
          id: v.id,
          code: v.code.trim().toUpperCase(),
          name: v.name.trim(),
          product_type: v.product_type || null,
          num_ante: parseInt(v.num_ante) || 0,
          notes: v.notes || null
        }));
      const { data } = await axios.put(`${API}/settings/ai-vocabulary`, { vocabulary: clean });
      setAiVocabulary(data.vocabulary || []);
      toast.success('Vocabolario IA salvato');
    } catch (err) {
      toast.error('Errore nel salvataggio del vocabolario');
    } finally {
      setSavingAiVocabulary(false);
    }
  };

  const saveExtraLibrary = async () => {
    setSavingExtraLibrary(true);
    try {
      const clean = (extraLibrary || [])
        .filter(p => p.name && p.name.trim())
        .map(p => ({
          id: p.id,
          name: p.name.trim(),
          unit_price: parseFloat(p.unit_price) || 0,
          iva: parseFloat(p.iva) || 22,
          description: p.description || '',
          unit_of_measure: p.unit_of_measure || null,
          aliases: (p.aliases || '').trim() || null
        }));
      const { data } = await axios.put(`${API}/settings/extra-products-library`, { library: clean });
      setExtraLibrary(data.library || []);
      toast.success('Libreria prodotti extra salvata');
    } catch (err) {
      toast.error('Errore nel salvataggio della libreria');
    } finally {
      setSavingExtraLibrary(false);
    }
  };

  // Load price list and custom profiles from backend on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [priceRes, profilesRes, libRes, vocabRes, statsRes, lessonsRes] = await Promise.all([
          axios.get(`${API}/settings/price-list`).catch(() => ({ data: { price_list: {} } })),
          axios.get(`${API}/settings/custom-profiles`).catch(() => ({ data: { custom_profiles: [] } })),
          axios.get(`${API}/settings/extra-products-library`).catch(() => ({ data: { library: [] } })),
          axios.get(`${API}/settings/ai-vocabulary`).catch(() => ({ data: { vocabulary: [] } })),
          axios.get(`${API}/settings/ai-stats`).catch(() => ({ data: null })),
          axios.get(`${API}/settings/ai-lessons`).catch(() => ({ data: { lessons: [] } }))
        ]);
        
        if (priceRes.data.price_list && Object.keys(priceRes.data.price_list).length > 0) {
          setPriceList(priceRes.data.price_list);
        } else {
          initializeDefaultPrices();
        }
        setCustomProfiles(profilesRes.data.custom_profiles || []);
        setExtraLibrary(libRes.data.library || []);
        setAiVocabulary(vocabRes.data.vocabulary || []);
        setAiStats(statsRes.data);
        setAiLessons(lessonsRes.data.lessons || []);
        // Load data health (parallel but non-blocking)
        axios.get(`${API}/settings/data-health`).then(r => setHealthData(r.data)).catch(() => {});
      } catch (err) {
        initializeDefaultPrices();
      }
    };
    loadData();
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/templates`);
      setWaTemplates(Array.isArray(data) ? data : [])
    } catch (err) { console.error('Error loading templates:', err); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Quote template settings
  const fetchQuoteTemplate = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/quote-template`);
      if (data.quote_template) setQuoteTemplate(prev => ({ ...prev, ...data.quote_template }));
    } catch (err) { console.error('Error loading quote template:', err); }
  }, []);

  useEffect(() => { fetchQuoteTemplate(); }, [fetchQuoteTemplate]);

  const saveQuoteTemplate = async () => {
    setSavingTemplate2(true);
    try {
      await axios.put(`${API}/settings/quote-template`, { quote_template: quoteTemplate });
      toast.success(t('settings.quoteTemplate.saved'));
    } catch (err) {
      toast.error(t('settings.quoteTemplate.saveError'));
    }
    setSavingTemplate2(false);
  };

  // Email settings
  const fetchEmailSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/email`);
      setEmailSettingsLoaded(data || {})
      setEmailSettings({ resend_api_key: '', sender_email: data.sender_email || '', sender_name: data.sender_name || '', use_own_domain: !!data.use_own_domain });
      if (data.use_own_domain || !data.platform_available) setShowEmailAdvanced(true);
    } catch (err) { console.error('Error loading email settings:', err); }
  }, []);

  useEffect(() => { fetchEmailSettings(); }, [fetchEmailSettings]);

  const saveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      const payload = { sender_name: emailSettings.sender_name.trim(), use_own_domain: emailSettings.use_own_domain };
      if (emailSettings.resend_api_key) payload.resend_api_key = emailSettings.resend_api_key;
      if (emailSettings.sender_email) payload.sender_email = emailSettings.sender_email;
      await axios.put(`${API}/settings/email`, payload);
      toast.success('Impostazioni email salvate');
      fetchEmailSettings();
      setEmailSettings(prev => ({ ...prev, resend_api_key: '' }));
    } catch (err) { toast.error('Errore nel salvataggio'); }
    finally { setSavingEmail(false); }
  };

  const testEmailConnection = async () => {
    setTestingEmail(true);
    try {
      const { data } = await axios.post(`${API}/email/test`);
      toast.success(data.message || 'Email di test inviata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel test');
    } finally { setTestingEmail(false); }
  };

  // SDI settings
  const fetchSDISettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/sdi`);
      setSdiConfig(prev => ({
        ...prev,
        oauth_token: data.oauth_token || prev.oauth_token || '',
        fiscal_id: data.fiscal_id || prev.fiscal_id || '',
        company_name: data.company_name || prev.company_name || '',
        company_address: data.company_address || prev.company_address || '',
        company_cap: data.company_cap || prev.company_cap || '',
        company_city: data.company_city || prev.company_city || '',
        company_province: data.company_province || prev.company_province || '',
        company_pec: data.company_pec || prev.company_pec || '',
        regime_fiscale: data.regime_fiscale || prev.regime_fiscale || 'RF01',
        business_registered: data.business_registered || false,
      }));
      setSdiLoaded(true);
    } catch (err) { console.error('Error loading SDI settings:', err); }
  }, []);

  const fetchWaNotifSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/whatsapp-notifications`);
      setWaNotifConfig(data || {})
      setWaNotifLoaded(true);
    } catch (err) { console.error('Error loading WA notif settings:', err); }
  }, []);

  useEffect(() => { if (ACCOUNTING_LAUNCHED) fetchSDISettings(); }, [fetchSDISettings]);
  useEffect(() => { fetchWaNotifSettings(); }, [fetchWaNotifSettings]);

  const saveSDISettings = async () => {
    setSavingSDI(true);
    try {
      await axios.put(`${API}/settings/sdi`, sdiConfig);
      toast.success('Configurazione SDI salvata');
      fetchSDISettings();
    } catch (err) { toast.error('Errore nel salvataggio SDI'); }
    finally { setSavingSDI(false); }
  };

  const handleRegisterBusiness = async () => {
    setRegisteringBusiness(true);
    try {
      const { data } = await axios.post(`${API}/sdi/register-business`);
      toast.success(data.message || 'Azienda registrata su OpenAPI.it');
      fetchSDISettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella registrazione');
    } finally { setRegisteringBusiness(false); }
  };

  const saveWaNotifSettings = async () => {
    setSavingWaNotif(true);
    try {
      await axios.put(`${API}/settings/whatsapp-notifications`, waNotifConfig);
      toast.success('Impostazioni notifiche WhatsApp salvate');
    } catch (err) { toast.error('Errore nel salvataggio'); }
    finally { setSavingWaNotif(false); }
  };

  const testWhatsApp = async () => {
    setTestingWa(true);
    try {
      const { data } = await axios.post(`${API}/whatsapp/test`);
      toast.success(data.message || 'Messaggio test inviato!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio del test');
    } finally { setTestingWa(false); }
  };


  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', category: 'custom', body: '', icon: 'ChatCircleDots' });
    setIsTemplateDialogOpen(true);
  };

  const openEditTemplate = (tmpl) => {
    setEditingTemplate(tmpl);
    setTemplateForm({ name: tmpl.name, category: tmpl.category, body: tmpl.body, icon: tmpl.icon || 'ChatCircleDots' });
    setIsTemplateDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.body.trim()) {
      toast.error('Nome e testo del template sono obbligatori');
      return;
    }
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/whatsapp/templates/${editingTemplate.id}`, templateForm);
        toast.success('Template aggiornato');
      } else {
        await axios.post(`${API}/whatsapp/templates`, templateForm);
        toast.success('Template creato');
      }
      setIsTemplateDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error('Errore nel salvataggio del template');
    } finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (tmpl) => {
    try {
      await axios.delete(`${API}/whatsapp/templates/${tmpl.id}`);
      toast.success(`Template "${tmpl.name}" eliminato`);
      fetchTemplates();
    } catch (err) { toast.error('Errore nell\'eliminazione'); }
  };

  const duplicateTemplate = (tmpl) => {
    setEditingTemplate(null);
    setTemplateForm({ name: `${tmpl.name} (copia)`, category: tmpl.category, body: tmpl.body, icon: tmpl.icon || 'ChatCircleDots' });
    setIsTemplateDialogOpen(true);
  };

  // CSV for price list
  const exportPriceListCsv = () => {
    const allProfiles = [...profileTypes, ...customProfiles];
    const headers = ['category', 'subcategory', ...allProfiles.map(p => p.name || p.id)];
    const csvRows = [headers.join(',')];
    productCategories.forEach(cat => {
      cat.subcategories.forEach(sub => {
        const row = [`"${cat.name}"`, `"${sub.name}"`];
        allProfiles.forEach(p => {
          const pid = p.id;
          row.push(priceList[sub.id]?.[pid] || '');
        });
        csvRows.push(row.join(','));
      });
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'listino_prezzi.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success(t('settings.priceList.exportSuccess'));
  };

  const importPriceListCsv = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { toast.error(t('csv.invalidFormat')); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      // headers: category, subcategory, profile1, profile2, ...
      const allProfiles = [...profileTypes, ...customProfiles];
      const profileHeaders = headers.slice(2);
      const newPriceList = { ...priceList };
      let imported = 0;
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const subName = vals[1];
        // Find subcategory by name
        let subId = null;
        for (const cat of productCategories) {
          const found = cat.subcategories.find(s => s.name === subName);
          if (found) { subId = found.id; break; }
        }
        if (!subId) continue;
        profileHeaders.forEach((pName, idx) => {
          const profile = allProfiles.find(p => (p.name || p.id) === pName);
          if (profile && vals[idx + 2]) {
            if (!newPriceList[subId]) newPriceList[subId] = {};
            newPriceList[subId][profile.id] = vals[idx + 2];
            imported++;
          }
        });
      }
      setPriceList(newPriceList);
      toast.success(t('settings.priceList.importSuccess') + ` (${imported} ${i18n.language === 'es' ? 'precios' : 'prezzi'})`);
    };
    input.click();
  };

  const initializeDefaultPrices = () => {
    const defaults = {};
    productCategories.forEach(cat => {
      cat.subcategories.forEach(sub => {
        defaults[sub.id] = {};
        profileTypes.forEach(profile => {
          // Apply default multiplier to base price
          defaults[sub.id][profile.id] = Math.round(sub.defaultPrice * profile.defaultMultiplier);
        });
      });
    });
    setPriceList(defaults);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.put(`${API}/settings/company`, formData);
      updateUser(data);
      toast.success('Impostazioni salvate');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploadingLogo(true);
    try {
      const { data } = await axios.post(`${API}/upload/logo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ company_logo: data.path });
      toast.success('Logo caricato');
    } catch (err) {
      console.error('Error uploading logo:', err);
      toast.error('Errore nel caricamento');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handlePriceChange = (subcategoryId, profileId, value) => {
    setPriceList(prev => ({
      ...prev,
      [subcategoryId]: {
        ...(prev[subcategoryId] || {}),
        [profileId]: parseFloat(value) || 0
      }
    }));
  };

  const savePriceList = async () => {
    setSavingPrices(true);
    try {
      await axios.put(`${API}/settings/price-list`, { price_list: priceList });
      toast.success('Listino prezzi salvato');
    } catch (err) {
      console.error('Error saving price list:', err);
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingPrices(false);
    }
  };

  const getPrice = (subcategoryId, profileId) => {
    return priceList[subcategoryId]?.[profileId] || '';
  };

  // Get calculated price for a custom profile
  const getCustomProfilePrice = (subcategoryId, customProfile) => {
    const parentPrice = parseFloat(priceList[subcategoryId]?.[customProfile.parent_profile_id]) || 0;
    return Math.round(parentPrice * (1 + customProfile.percentage_markup / 100));
  };

  // Get all profiles for a parent
  const getChildProfiles = (parentId) => {
    return (customProfiles || []).filter(cp => cp.parent_profile_id === parentId);
  };

  const handleAddProfile = async () => {
    if (!newProfile.name.trim()) {
      toast.error('Inserisci un nome per il profilo');
      return;
    }
    try {
      const { data } = await axios.post(`${API}/settings/custom-profiles`, newProfile);
      setCustomProfiles(prev => [...prev, data]);
      setIsAddProfileOpen(false);
      setNewProfile({ name: '', parent_profile_id: 'pvc', percentage_markup: 10, description: '', pricing: 'own' });
      toast.success(data.pricing === 'own'
        ? `Profilo "${data.name}" aggiunto: imposta i suoi prezzi nel Listino prezzi`
        : `Profilo "${data.name}" aggiunto`);
    } catch (err) {
      toast.error('Errore nella creazione del profilo');
    }
  };

  const handleCopyProfile = (sourceProfile) => {
    setNewProfile({
      name: `${sourceProfile.name} (copia)`,
      parent_profile_id: sourceProfile.parent_profile_id,
      percentage_markup: sourceProfile.percentage_markup,
      description: sourceProfile.description || '',
      pricing: sourceProfile.pricing || 'markup'
    });
    setIsAddProfileOpen(true);
  };

  const handleDeleteProfile = async (profileId, profileName) => {
    try {
      await axios.delete(`${API}/settings/custom-profiles/${profileId}`);
      setCustomProfiles(prev => prev.filter(p => p.id !== profileId));
      toast.success(`Profilo "${profileName}" eliminato`);
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">
          {t('settings.title')}
        </h1>
        <p className="text-[#4A5568] mt-1">
          {t('settings.subtitle')}
        </p>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-auto min-w-full sm:flex sm:flex-wrap gap-1 h-auto p-1">
            <TabsTrigger value="company" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2">
              <Buildings size={16} />
              <span className="hidden sm:inline">{t('settings.tabs.company')}</span>
              <span className="sm:hidden">Azienda</span>
            </TabsTrigger>
            <TabsTrigger value="prices" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2">
              <ListBullets size={16} />
              <span className="hidden sm:inline">{t('settings.tabs.priceList')}</span>
              <span className="sm:hidden">Prezzi</span>
            </TabsTrigger>
            <TabsTrigger value="product-options" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-product-options">
              <Palette size={16} />
              <span className="hidden sm:inline">Opzioni prodotto</span>
              <span className="sm:hidden">Opzioni</span>
            </TabsTrigger>
            <TabsTrigger value="extra-library" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-extra-library">
              <Plus size={16} />
              <span className="hidden sm:inline">Libreria Extra</span>
              <span className="sm:hidden">Extra</span>
            </TabsTrigger>
            <TabsTrigger value="ai-vocabulary" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-ai-vocabulary">
              <Sparkle size={16} />
              <span className="hidden sm:inline">Vocabolario IA</span>
              <span className="sm:hidden">IA</span>
            </TabsTrigger>
            <TabsTrigger value="data-health" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-data-health">
              <ShieldCheck size={16} />
              <span className="hidden sm:inline">Integrità Dati</span>
              <span className="sm:hidden">Dati</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-templates">
              <WhatsappLogo size={16} />
              <span className="hidden sm:inline">{t('settings.tabs.templatesWa')}</span>
              <span className="sm:hidden">WA</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-email">
              <EnvelopeSimple size={16} />
              Email
            </TabsTrigger>
            <TabsTrigger value="quote-template" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-quote-template">
              <FileText size={16} />
              <span className="hidden sm:inline">{t('settings.tabs.quoteTemplate')}</span>
              <span className="sm:hidden">Template</span>
            </TabsTrigger>
            {ACCOUNTING_LAUNCHED && (
            <TabsTrigger value="sdi" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-sdi">
              <PaperPlaneTilt size={16} />
              <span className="hidden sm:inline">Fatturazione SDI</span>
              <span className="sm:hidden">SDI</span>
            </TabsTrigger>
            )}
            {WHATSAPP_ALERTS_LAUNCHED && (
            <TabsTrigger value="wa-notifications" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-wa-notifications">
              <ChatCircleDots size={16} />
              <span className="hidden sm:inline">Notifiche WA</span>
              <span className="sm:hidden">WA</span>
            </TabsTrigger>
            )}
            <TabsTrigger value="push-notifications" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-push-notifications">
              <Bell size={16} />
              <span className="hidden sm:inline">Notifiche Push</span>
              <span className="sm:hidden">Push</span>
            </TabsTrigger>
            <TabsTrigger value="subscription" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-subscription">
              <CreditCard size={16} />
              <span className="hidden sm:inline">Abbonamento</span>
              <span className="sm:hidden">Piano</span>
            </TabsTrigger>
            {user?.is_platform_admin && (
              <TabsTrigger value="admin-users" className="flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 py-2" data-testid="tab-admin-users">
                <Users size={16} />
                <span>Iscritti</span>
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Company Info Tab */}
        <TabsContent value="company" className="mt-6">
          <div className="max-w-3xl space-y-6">
            {/* Company Info Card */}
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardHeader className="border-b border-[#E2E8F0] pb-4">
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                  <Buildings size={20} className="text-[#0033A0]" />
                  {t('settings.company.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Logo Upload */}
                  <div>
                    <Label>{t('common.uploadLogo')}</Label>
                    <div className="mt-2 flex items-center gap-4">
                      <div className="w-24 h-24 border-2 border-dashed border-[#E2E8F0] rounded-sm flex items-center justify-center bg-[#F4F6F8]">
                        {user?.company_logo ? (
                          <img
                            src={`${API}/files/${user.company_logo}`}
                            alt="Logo"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Buildings size={32} className="text-[#4A5568]" />
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                          data-testid="logo-upload-input"
                        />
                        <label
                          htmlFor="logo-upload"
                          className="inline-flex items-center gap-2 px-4 py-2 border border-[#E2E8F0] rounded-sm text-sm font-medium text-[#0A0F1A] hover:bg-[#F4F6F8] cursor-pointer transition-colors"
                        >
                          {uploadingLogo ? 'Caricamento...' : (
                            <>
                              <Upload size={16} />
                              {t('common.uploadLogo')}
                            </>
                          )}
                        </label>
                        <p className="text-xs text-[#4A5568] mt-1">
                          PNG, JPG, GIF fino a 2MB
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="company_name">{t('settings.company.name')}</Label>
                      <Input
                        id="company_name"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        className="mt-1"
                        placeholder="es. Serramenti Rossi S.r.l."
                        data-testid="company-name-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="company_address">{t('settings.company.address')}</Label>
                      <Input
                        id="company_address"
                        value={formData.company_address}
                        onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                        className="mt-1"
                        placeholder="es. Via Roma 123, 00100 Roma (RM)"
                        data-testid="company-address-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company_phone">{t('settings.company.phone')}</Label>
                      <Input
                        id="company_phone"
                        value={formData.company_phone}
                        onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                        className="mt-1"
                        placeholder="es. +39 06 1234567"
                        data-testid="company-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company_email">{t('settings.company.email')}</Label>
                      <Input
                        id="company_email"
                        type="email"
                        value={formData.company_email}
                        onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                        className="mt-1"
                        placeholder="es. info@serramenti.it"
                        data-testid="company-email-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="company_vat">{t('settings.company.vat')}</Label>
                      <Input
                        id="company_vat"
                        value={formData.company_vat}
                        onChange={(e) => setFormData({ ...formData, company_vat: e.target.value })}
                        className="mt-1"
                        placeholder="es. IT12345678901"
                        data-testid="company-vat-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="terms_conditions">{t('settings.company.terms')}</Label>
                    <Textarea
                      id="terms_conditions"
                      value={formData.terms_conditions}
                      onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                      className="mt-1"
                      rows={6}
                      placeholder="Inserisci i termini e condizioni che appariranno nei preventivi..."
                      data-testid="terms-conditions-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="default_quote_notes">Note predefinite del preventivo</Label>
                    <Textarea
                      id="default_quote_notes"
                      value={formData.default_quote_notes}
                      onChange={(e) => setFormData({ ...formData, default_quote_notes: e.target.value })}
                      className="mt-1"
                      rows={4}
                      placeholder="es. I prezzi includono trasporto e posa in opera. Smaltimento dei vecchi infissi a carico nostro."
                      data-testid="default-quote-notes-input"
                    />
                    <p className="text-xs text-[#4A5568] mt-1">
                      Questo testo viene inserito automaticamente nelle note di ogni nuovo preventivo (anche quelli creati dal Sopralluogo IA). Puoi sempre modificarlo nel singolo preventivo.
                    </p>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-[#E2E8F0]">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                      data-testid="save-settings-button"
                    >
                      {loading ? 'Salvataggio...' : (
                        <>
                          <Check size={18} className="mr-2" />
                          {t('settings.company.save')}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Account Info */}
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardHeader className="border-b border-[#E2E8F0] pb-4">
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A]">
                  Informazioni Account
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-[#E2E8F0] gap-3">
                    <div className="flex-1">
                      <p className="text-sm text-[#4A5568] mb-1">Nome</p>
                      {editingName ? (
                        <div className="flex gap-2">
                          <Input
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            className="h-9 text-sm"
                            data-testid="account-name-input"
                          />
                          <Button
                            size="sm" onClick={saveName} disabled={savingName}
                            className="bg-[#0033A0] hover:bg-[#0047AB] text-white h-9"
                            data-testid="account-name-save"
                          >
                            {savingName ? '...' : <Check size={16} />}
                          </Button>
                          <Button
                            size="sm" variant="outline" onClick={() => setEditingName(false)}
                            className="h-9"
                            data-testid="account-name-cancel"
                          >
                            Annulla
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#0A0F1A]">{user?.name}</p>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setNameDraft(user?.name || ''); setEditingName(true); }}
                            className="h-7 w-7 p-0 text-[#4A5568] hover:text-[#0033A0]"
                            data-testid="account-name-edit"
                            title="Modifica nome"
                          >
                            <PencilSimple size={14} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-[#E2E8F0]">
                    <div>
                      <p className="text-sm text-[#4A5568]">Email</p>
                      <p className="font-medium text-[#0A0F1A]">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm text-[#4A5568]">Ruolo</p>
                      <p className="font-medium text-[#0A0F1A] capitalize">{user?.role}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* The company's own profiles, colours, glass types and glazing beads */}
        <TabsContent value="product-options" className="mt-6">
          <ProductOptionsSettings
            customProfiles={customProfiles}
            onNewProfile={() => setIsAddProfileOpen(true)}
            onDeleteProfile={handleDeleteProfile}
          />
        </TabsContent>

        {/* Price List Tab */}
        <TabsContent value="prices" className="mt-6">
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                  <CurrencyEur size={20} className="text-[#0033A0]" />
                  Listino Prezzi al m² per Profilo
                </CardTitle>
                <p className="text-sm text-[#4A5568] mt-1">
                  {i18n.language === 'es' ? 'Establece precios diferentes para cada tipo de perfil' : 'Imposta prezzi diversi per ogni tipologia di profilo'}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={exportPriceListCsv} className="border-[#0033A0] text-[#0033A0]" data-testid="export-prices-btn">
                  <Download size={16} className="mr-1" /> {t('common.export')} CSV
                </Button>
                <Button variant="outline" onClick={importPriceListCsv} className="border-[#0033A0] text-[#0033A0]" data-testid="import-prices-btn">
                  <Upload size={16} className="mr-1" /> {t('common.import')} CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsAddProfileOpen(true)}
                  className="border-[#0033A0] text-[#0033A0] hover:bg-[#F4F6F8]"
                  data-testid="add-custom-profile-button"
                >
                  <Plus size={18} className="mr-1" />
                  Nuovo Profilo
                </Button>
                <Button
                  onClick={savePriceList}
                  disabled={savingPrices}
                  className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                  data-testid="save-price-list-button"
                >
                  {savingPrices ? 'Salvataggio...' : (
                    <>
                      <Check size={18} className="mr-2" />
                      Salva Listino
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {/* Profile Types Legend + Custom Profiles */}
              <div className="mb-6 p-4 bg-[#F4F6F8] rounded-sm">
                <h4 className="text-sm font-semibold text-[#0A0F1A] mb-3">Profili Base</h4>
                <div className="flex flex-wrap gap-3">
                  {profileTypes.map((profile) => {
                    const childCount = getChildProfiles(profile.id).length;
                    return (
                      <div key={profile.id} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-sm border border-[#E2E8F0]">
                        <span className="w-3 h-3 rounded-full" style={{ 
                          backgroundColor: profile.id === 'pvc' ? '#3B82F6' :
                            profile.id === 'alluminio_freddo' ? '#6B7280' :
                            profile.id === 'alluminio_termico' ? '#F59E0B' :
                            profile.id === 'legno' ? '#92400E' :
                            profile.id === 'legno_alluminio' ? '#7C3AED' :
                            profile.id === 'alluminio_legno' ? '#EC4899' :
                            '#10B981'
                        }} />
                        <span className="text-xs font-medium text-[#0A0F1A]">{profile.name}</span>
                        {childCount > 0 && (
                          <span className="text-[10px] bg-[#0033A0] text-white px-1.5 py-0.5 rounded-full">{childCount}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {(customProfiles || []).length > 0 && (
                  <>
                    <h4 className="text-sm font-semibold text-[#0A0F1A] mt-4 mb-3">Profili Personalizzati</h4>
                    <div className="flex flex-wrap gap-3">
                      {(customProfiles || []).map((cp) => {
                        const parent = profileTypes.find(p => p.id === cp.parent_profile_id);
                        return (
                          <div key={cp.id} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-sm border border-dashed border-[#0033A0]">
                            <div className="flex flex-col">
                              <span className="text-xs font-medium text-[#0A0F1A]">{cp.name}</span>
                              {cp.description && (
                                <span className="text-[10px] text-[#4A5568] max-w-[200px] truncate" title={cp.description}>{cp.description}</span>
                              )}
                            </div>
                            <span className="text-[10px] bg-[#E8F0FE] text-[#0033A0] px-1.5 py-0.5 rounded-sm">
                              {cp.pricing === 'own' ? 'prezzi propri' : `${parent?.shortName} +${cp.percentage_markup}%`}
                            </span>
                            <button
                              onClick={() => handleCopyProfile(cp)}
                              className="text-[#0033A0] hover:text-[#0047AB] ml-1"
                              title="Copia profilo"
                              data-testid={`copy-profile-${cp.id}`}
                            >
                              <Copy size={12} />
                            </button>
                            <button
                              onClick={() => handleDeleteProfile(cp.id, cp.name)}
                              className="text-red-400 hover:text-red-600"
                              data-testid={`delete-profile-${cp.id}`}
                            >
                              <Trash size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Categories with expandable subcategories */}
              <div className="space-y-4">
                {productCategories.map((category) => (
                  <div key={category.id} className="border border-[#E2E8F0] rounded-sm overflow-hidden">
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full bg-[#F4F6F8] px-4 py-3 flex items-center justify-between hover:bg-[#E2E8F0] transition-colors"
                      data-testid={`toggle-category-${category.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-sm p-1.5 border border-[#E2E8F0]">
                          <ProductIcon type={category.icon} className="w-full h-full" />
                        </div>
                        <h3 className="font-semibold text-[#0A0F1A]">{category.name}</h3>
                        <span className="text-xs text-[#4A5568] bg-white px-2 py-0.5 rounded-sm">
                          {category.subcategories.length} tipologie
                        </span>
                      </div>
                      {expandedCategories[category.id] ? (
                        <CaretDown size={20} className="text-[#4A5568]" />
                      ) : (
                        <CaretRight size={20} className="text-[#4A5568]" />
                      )}
                    </button>

                    {/* Subcategories with price grid */}
                    {expandedCategories[category.id] && (
                      <div className="p-4">
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[800px]">
                            <thead>
                              <tr className="border-b border-[#E2E8F0]">
                                <th className="text-left py-2 px-3 text-xs font-semibold text-[#4A5568] uppercase tracking-wider w-48">
                                  Tipologia
                                </th>
                                {profileTypes.map((profile) => (
                                  <React.Fragment key={profile.id}>
                                    <th className="text-center py-2 px-2 text-xs font-semibold text-[#4A5568] uppercase tracking-wider">
                                      {profile.shortName}
                                    </th>
                                    {getChildProfiles(profile.id).map((cp) => (
                                      <th key={cp.id} className="text-center py-2 px-2 text-xs font-semibold text-[#0033A0] uppercase tracking-wider bg-[#F8FAFF]">
                                        <div className="flex flex-col items-center">
                                          <span className="truncate max-w-[80px]" title={cp.name}>{cp.name}</span>
                                          <span className="text-[10px] font-normal text-[#4A5568]">{cp.pricing === 'own' ? 'prezzi propri' : `+${cp.percentage_markup}%`}</span>
                                        </div>
                                      </th>
                                    ))}
                                  </React.Fragment>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {category.subcategories.map((sub) => (
                                <tr key={sub.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#F4F6F8]">
                                  <td className="py-3 px-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-[#F4F6F8] rounded-sm p-1">
                                        <ProductIcon type={sub.icon} className="w-full h-full" />
                                      </div>
                                      <span className="text-sm font-medium text-[#0A0F1A]">{sub.name}</span>
                                    </div>
                                  </td>
                                  {profileTypes.map((profile) => (
                                    <React.Fragment key={profile.id}>
                                      <td className="py-2 px-2">
                                        <div className="relative">
                                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#4A5568] text-xs">€</span>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={getPrice(sub.id, profile.id)}
                                            onChange={(e) => handlePriceChange(sub.id, profile.id, e.target.value)}
                                            className="pl-5 pr-8 h-8 text-sm text-center w-24"
                                            data-testid={`price-${sub.id}-${profile.id}`}
                                          />
                                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#4A5568] text-xs">/m²</span>
                                        </div>
                                      </td>
                                      {getChildProfiles(profile.id).map((cp) => (
                                        <td key={cp.id} className="py-2 px-2 bg-[#F8FAFF]">
                                          <div className="relative">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#4A5568] text-xs">€</span>
                                            {cp.pricing === 'own' ? (
                                              <Input
                                                type="number"
                                                step="0.01"
                                                value={getPrice(sub.id, cp.id)}
                                                placeholder={String(priceList[sub.id]?.[cp.parent_profile_id] || '')}
                                                onChange={(e) => handlePriceChange(sub.id, cp.id, e.target.value)}
                                                className="pl-5 pr-8 h-8 text-sm text-center w-24 border-[#0033A0]/40"
                                                data-testid={`price-${sub.id}-${cp.id}`}
                                              />
                                            ) : (
                                              <Input
                                                type="number"
                                                value={getCustomProfilePrice(sub.id, cp)}
                                                readOnly
                                                className="pl-5 pr-8 h-8 text-sm text-center w-24 bg-[#F0F4FF] text-[#0033A0] font-medium cursor-default"
                                                data-testid={`price-${sub.id}-${cp.id}`}
                                              />
                                            )}
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#4A5568] text-xs">/m²</span>
                                          </div>
                                        </td>
                                      ))}
                                    </React.Fragment>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Extra Products Library Tab */}
        <TabsContent value="extra-library" className="mt-6">
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4 flex flex-row items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                  <Plus size={20} className="text-[#0033A0]" />
                  Libreria Prodotti Extra
                </CardTitle>
                <p className="text-sm text-[#4A5568] mt-1">
                  Prodotti aggiuntivi riutilizzabili (es. cielini, davanzali, trasporto) selezionabili con un click nel Preventivo.
                  Il Sopralluogo IA li riconosce dal nome o dagli altri nomi e li prezza da solo: al metro lineare (lato più lungo), al m² o al pezzo.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setExtraLibrary([...(extraLibrary || []), { id: '', name: '', unit_price: 0, iva: 22, description: '' }])}
                  className="border-[#0033A0] text-[#0033A0]"
                  data-testid="add-extra-library-item"
                >
                  <Plus size={16} className="mr-1" /> Nuovo Prodotto
                </Button>
                <Button
                  onClick={saveExtraLibrary}
                  disabled={savingExtraLibrary}
                  className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                  data-testid="save-extra-library-button"
                >
                  {savingExtraLibrary ? 'Salvataggio...' : (<><Check size={18} className="mr-2" />Salva Libreria</>)}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {(extraLibrary || []).length === 0 ? (
                <div className="text-center py-12 text-[#4A5568]">
                  <p className="text-sm mb-2">Nessun prodotto in libreria</p>
                  <p className="text-xs text-[#A0AEC0]">Aggiungi i prodotti che usi più spesso per velocizzare la creazione dei preventivi</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(extraLibrary || []).map((prod, idx) => (
                    <div key={prod.id || `new-${idx}`} className="grid grid-cols-12 gap-2 items-center p-3 bg-[#F4F6F8] rounded-sm" data-testid={`extra-lib-row-${idx}`}>
                      <div className="col-span-12 sm:col-span-3">
                        <Label className="text-[10px] text-[#4A5568]">Nome prodotto</Label>
                        <Input
                          placeholder="Es. Zanzariera"
                          value={prod.name || ''}
                          onChange={(e) => {
                            const arr = [...extraLibrary];
                            arr[idx] = { ...arr[idx], name: e.target.value };
                            setExtraLibrary(arr);
                          }}
                          className="h-9 text-sm mt-1"
                          data-testid={`extra-lib-name-${idx}`}
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-3">
                        <Label className="text-[10px] text-[#4A5568]">Descrizione (opzionale)</Label>
                        <Input
                          placeholder="Note o specifiche"
                          value={prod.description || ''}
                          onChange={(e) => {
                            const arr = [...extraLibrary];
                            arr[idx] = { ...arr[idx], description: e.target.value };
                            setExtraLibrary(arr);
                          }}
                          className="h-9 text-sm mt-1"
                          data-testid={`extra-lib-desc-${idx}`}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-[10px] text-[#4A5568]">Prezzo (€)</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          value={prod.unit_price ?? ''}
                          onChange={(e) => {
                            const arr = [...extraLibrary];
                            arr[idx] = { ...arr[idx], unit_price: e.target.value };
                            setExtraLibrary(arr);
                          }}
                          className="h-9 text-sm mt-1"
                          data-testid={`extra-lib-price-${idx}`}
                        />
                      </div>
                      <div className="col-span-3 sm:col-span-1">
                        <Label className="text-[10px] text-[#4A5568]">IVA</Label>
                        <Select
                          value={String(prod.iva ?? 22)}
                          onValueChange={(v) => {
                            const arr = [...extraLibrary];
                            arr[idx] = { ...arr[idx], iva: parseFloat(v) };
                            setExtraLibrary(arr);
                          }}
                        >
                          <SelectTrigger className="h-9 text-xs mt-1" data-testid={`extra-lib-iva-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">4%</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="22">22%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        <Label className="text-[10px] text-[#4A5568]">Unità</Label>
                        <Select
                          value={prod.unit_of_measure || '_none'}
                          onValueChange={(v) => {
                            const arr = [...extraLibrary];
                            arr[idx] = { ...arr[idx], unit_of_measure: v === '_none' ? '' : v };
                            setExtraLibrary(arr);
                          }}
                        >
                          <SelectTrigger className="h-9 text-xs mt-1" data-testid={`extra-lib-unit-${idx}`}><SelectValue placeholder="Nessuna" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none"><span className="text-[#A0AEC0] italic">Nessuna</span></SelectItem>
                            <SelectItem value="Pezzi">Pezzi</SelectItem>
                            <SelectItem value="Kg">Kg</SelectItem>
                            <SelectItem value="MQ">MQ (al m²)</SelectItem>
                            <SelectItem value="ML">ML (al metro)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 sm:col-span-1 flex items-end justify-end h-full">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setExtraLibrary(extraLibrary.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:bg-red-50 h-9 w-9 p-0"
                          data-testid={`extra-lib-remove-${idx}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                      <div className="col-span-12">
                        <Label className="text-[10px] text-[#4A5568]">Altri nomi o sigle per l'IA (opzionale, separati da virgola)</Label>
                        <Input
                          placeholder="Es. celino, CIEL"
                          value={prod.aliases || ''}
                          onChange={(e) => {
                            const arr = [...extraLibrary];
                            arr[idx] = { ...arr[idx], aliases: e.target.value };
                            setExtraLibrary(arr);
                          }}
                          className="h-9 text-sm mt-1"
                          data-testid={`extra-lib-aliases-${idx}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Vocabulary Tab */}
        <TabsContent value="ai-vocabulary" className="mt-6 space-y-4">
          {/* Stats panel */}
          {aiStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ai-stats-panel">
              <Card className="border border-[#E2E8F0] rounded-sm">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">Analisi eseguite</div>
                  <div className="text-2xl font-outfit font-bold text-[#0033A0] mt-1" data-testid="stat-total-analyses">
                    {aiStats.total_analyses || 0}
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-[#E2E8F0] rounded-sm">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">Prodotti estratti</div>
                  <div className="text-2xl font-outfit font-bold text-[#0033A0] mt-1" data-testid="stat-total-items">
                    {aiStats.total_items_extracted || 0}
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-[#E2E8F0] rounded-sm">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">Sigle in vocabolario</div>
                  <div className="text-2xl font-outfit font-bold text-[#0033A0] mt-1" data-testid="stat-vocab-count">
                    {aiStats.total_vocab_entries || 0}
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-[#E2E8F0] rounded-sm">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">Sigle più usate</div>
                  <div className="flex flex-wrap gap-1 mt-2" data-testid="stat-top-codes">
                    {(aiStats.top_codes || []).length === 0 ? (
                      <span className="text-xs text-[#A0AEC0] italic">Nessuna ancora</span>
                    ) : (
                      (aiStats.top_codes || []).map(tc => (
                        <span key={tc.code} className="text-[10px] font-mono px-2 py-0.5 bg-[#EEF2FF] text-[#0033A0] rounded">
                          {tc.code} ×{tc.count}
                        </span>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* AI Lessons (corrections learned from user) */}
          {(aiLessons || []).length > 0 && (
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardHeader className="border-b border-[#E2E8F0] pb-4">
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                  <PencilSimple size={20} className="text-[#0033A0]" />
                  Correzioni apprese dall'IA ({aiLessons.length})
                </CardTitle>
                <p className="text-sm text-[#4A5568] mt-1">
                  Correzioni che hai fatto durante i sopralluoghi. L'IA le usa come esempi per le prossime analisi (max 50, più recenti prima).
                </p>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {aiLessons.map(l => {
                    const fmt = (d) => [
                      d.raw_code && `${d.raw_code}`,
                      d.product_name,
                      d.product_type && `(${d.product_type}${d.num_ante ? ` ${d.num_ante} ante` : ''})`,
                      (d.width || d.height) && `${d.width || 0}x${d.height || 0}mm`,
                      d.opening_type && `ap.${d.opening_type}`,
                      d.material,
                      d.quantity > 1 && `x${d.quantity}`
                    ].filter(Boolean).join(' · ');
                    return (
                      <div key={l.id} className="flex items-start justify-between gap-2 p-2 bg-[#F4F6F8] rounded-sm text-xs" data-testid={`lesson-${l.id}`}>
                        <div className="flex-1">
                          <div className="text-[#4A5568] line-through">{fmt(l.original) || '(vuoto)'}</div>
                          <div className="text-[#0A0F1A] font-medium mt-0.5">→ {fmt(l.corrected) || '(vuoto)'}</div>
                          {l.note && <div className="text-[10px] text-[#4A5568] italic mt-1">Nota: {l.note}</div>}
                        </div>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => deleteLesson(l.id)}
                          className="text-red-500 hover:bg-red-50 h-7 w-7 p-0 shrink-0"
                          data-testid={`lesson-delete-${l.id}`}
                        >
                          <Trash size={12} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4 flex flex-row items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                  <Sparkle size={20} className="text-[#0033A0]" />
                  Vocabolario Sopralluogo IA
                </CardTitle>
                <p className="text-sm text-[#4A5568] mt-1">
                  Definisci le tue sigle personalizzate. L'IA le userà per riconoscere i prodotti nelle foto dei sopralluoghi.
                  <br />
                  <span className="text-xs text-[#A0AEC0]">Esempio: "FSC" → "Finestra Scorrevole Comando Remoto". Le sigle di settore standard (F1, PF2, TAP, ecc.) sono già riconosciute.</span>
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setAiVocabulary([...(aiVocabulary || []), { id: '', code: '', name: '', product_type: '', num_ante: 0, notes: '' }])}
                  className="border-[#0033A0] text-[#0033A0]"
                  data-testid="add-ai-vocab-item"
                >
                  <Plus size={16} className="mr-1" /> Nuova Sigla
                </Button>
                <Button
                  onClick={saveAiVocabulary}
                  disabled={savingAiVocabulary}
                  className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                  data-testid="save-ai-vocab-button"
                >
                  {savingAiVocabulary ? 'Salvataggio...' : (<><Check size={18} className="mr-2" />Salva Vocabolario</>)}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {(aiVocabulary || []).length === 0 ? (
                <div className="text-center py-12 text-[#4A5568]">
                  <p className="text-sm mb-2">Nessuna sigla personalizzata</p>
                  <p className="text-xs text-[#A0AEC0]">L'IA riconosce comunque le sigle standard del settore. Aggiungi qui le tue sigle specifiche per aziende o prodotti particolari.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(aiVocabulary || []).map((v, idx) => (
                    <div key={v.id || `new-${idx}`} className="grid grid-cols-12 gap-2 items-end p-3 bg-[#F4F6F8] rounded-sm" data-testid={`ai-vocab-row-${idx}`}>
                      <div className="col-span-6 sm:col-span-2">
                        <Label className="text-[10px] text-[#4A5568]">Sigla</Label>
                        <Input
                          placeholder="Es. FSC"
                          value={v.code || ''}
                          onChange={(e) => {
                            const arr = [...aiVocabulary];
                            arr[idx] = { ...arr[idx], code: e.target.value.toUpperCase() };
                            setAiVocabulary(arr);
                          }}
                          className="h-9 text-sm mt-1 font-mono uppercase"
                          data-testid={`ai-vocab-code-${idx}`}
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-4">
                        <Label className="text-[10px] text-[#4A5568]">Significato (espansione)</Label>
                        <Input
                          placeholder="Es. Finestra Scorrevole Comando Remoto"
                          value={v.name || ''}
                          onChange={(e) => {
                            const arr = [...aiVocabulary];
                            arr[idx] = { ...arr[idx], name: e.target.value };
                            setAiVocabulary(arr);
                          }}
                          className="h-9 text-sm mt-1"
                          data-testid={`ai-vocab-name-${idx}`}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <Label className="text-[10px] text-[#4A5568]">Tipo prodotto</Label>
                        <Select
                          value={v.product_type || '_none'}
                          onValueChange={(val) => {
                            const arr = [...aiVocabulary];
                            arr[idx] = { ...arr[idx], product_type: val === '_none' ? '' : val };
                            setAiVocabulary(arr);
                          }}
                        >
                          <SelectTrigger className="h-9 text-xs mt-1" data-testid={`ai-vocab-type-${idx}`}><SelectValue placeholder="Qualsiasi" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none"><span className="text-[#A0AEC0] italic">Qualsiasi</span></SelectItem>
                            <SelectItem value="finestra">Finestra</SelectItem>
                            <SelectItem value="portafinestra">Portafinestra</SelectItem>
                            <SelectItem value="porta_ingresso">Porta d'ingresso</SelectItem>
                            <SelectItem value="scorrevole">Scorrevole</SelectItem>
                            <SelectItem value="persiana">Persiana</SelectItem>
                            <SelectItem value="tapparella">Tapparella</SelectItem>
                            <SelectItem value="zanzariera">Zanzariera</SelectItem>
                            <SelectItem value="altro">Altro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-[10px] text-[#4A5568]">N° ante</Label>
                        <Input
                          type="number" min="0" max="10"
                          value={v.num_ante ?? 0}
                          onChange={(e) => {
                            const arr = [...aiVocabulary];
                            arr[idx] = { ...arr[idx], num_ante: parseInt(e.target.value) || 0 };
                            setAiVocabulary(arr);
                          }}
                          className="h-9 text-sm mt-1 text-center"
                          data-testid={`ai-vocab-ante-${idx}`}
                        />
                      </div>
                      <div className="col-span-2 sm:col-span-1 flex items-end justify-end h-full">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setAiVocabulary(aiVocabulary.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:bg-red-50 h-9 w-9 p-0"
                          data-testid={`ai-vocab-remove-${idx}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Health Tab */}
        <TabsContent value="data-health" className="mt-6 space-y-4">
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4 flex flex-row items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                  <ShieldCheck size={20} className="text-[#0033A0]" weight="fill" />
                  Integrità Dati
                </CardTitle>
                <p className="text-sm text-[#4A5568] mt-1">
                  Scansiona i tuoi dati alla ricerca di campi mancanti o inconsistenze. La normalizzazione applica fix sicuri (UUID e timestamp mancanti).
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={loadHealth}
                  data-testid="health-refresh"
                >
                  Ricontrolla
                </Button>
                <Button
                  onClick={runNormalize}
                  disabled={normalizing}
                  className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                  data-testid="health-normalize"
                >
                  {normalizing ? (
                    <><CircleNotch size={16} className="mr-2 animate-spin" />Normalizzazione...</>
                  ) : (
                    <><Check size={16} className="mr-2" />Normalizza Tutto</>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {!healthData ? (
                <p className="text-center text-sm text-[#4A5568] py-8">Caricamento dati in corso...</p>
              ) : (
                <div className="space-y-3">
                  {[
                    { key: 'clients', label: 'Clienti', icon: '👤' },
                    { key: 'quotes', label: 'Preventivi', icon: '📄' },
                    { key: 'follow_ups', label: 'Solleciti', icon: '🔔' },
                  ].map(({ key, label, icon }) => {
                    const s = healthData[key] || { total: 0, with_issues: 0, missing_fields: {} };
                    const healthy = s.with_issues === 0;
                    return (
                      <div key={key} className="border border-[#E2E8F0] rounded-sm p-4" data-testid={`health-${key}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{icon}</span>
                            <div>
                              <div className="font-medium text-[#0A0F1A]">{label}</div>
                              <div className="text-xs text-[#4A5568]">{s.total} documenti totali</div>
                            </div>
                          </div>
                          {healthy ? (
                            <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 bg-green-50 text-green-700 rounded-full">
                              <CheckCircle size={14} weight="fill" /> Tutto OK
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 bg-amber-50 text-amber-700 rounded-full">
                              <Warning size={14} weight="fill" /> {s.with_issues} con problemi
                            </span>
                          )}
                        </div>
                        {!healthy && (
                          <div className="mt-3 pt-3 border-t border-[#E2E8F0] grid grid-cols-2 md:grid-cols-4 gap-2">
                            {Object.entries(s.missing_fields).filter(([,n]) => n > 0).map(([field, n]) => (
                              <div key={field} className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                <span className="text-[#4A5568]">Senza </span>
                                <span className="font-mono font-medium text-amber-800">{field}</span>
                                <span className="text-amber-800 font-semibold ml-1">({n})</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <p className="text-xs text-[#A0AEC0] italic pt-2">
                    Nota: la normalizzazione risolve solo UUID e timestamp mancanti. Problemi come "senza client_id" richiedono intervento manuale (apri il preventivo e seleziona il cliente).
                  </p>
                  {healthData.checked_at && (
                    <p className="text-[10px] text-[#A0AEC0]">Ultima verifica: {new Date(healthData.checked_at).toLocaleString('it-IT')}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <div className="max-w-4xl space-y-6">
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardHeader className="border-b border-[#E2E8F0] pb-4 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                    <WhatsappLogo size={20} className="text-[#25D366]" />
                    {t('settings.waTemplates.title')}
                  </CardTitle>
                  <p className="text-sm text-[#4A5568] mt-1">
                    {t('settings.waTemplates.subtitle')}
                  </p>
                </div>
                <Button
                  onClick={openNewTemplate}
                  className="bg-[#25D366] hover:bg-[#1DA851] text-white"
                  data-testid="add-template-btn"
                >
                  <Plus size={18} className="mr-1" />
                  {t('settings.waTemplates.newTemplate')}
                </Button>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* Variables help */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                  <div className="flex items-start gap-2">
                    <Info size={16} className="text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-blue-800">{t('settings.waTemplates.variables')}</p>
                      <p className="text-xs text-blue-700 mt-1">
                        {t('settings.waTemplates.variablesHelp')}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[
                          { var: '{nome_cliente}', desc: 'Nome del cliente' },
                          { var: '{numero_preventivo}', desc: 'N. preventivo' },
                          { var: '{totale}', desc: 'Totale preventivo' },
                          { var: '{num_prodotti}', desc: 'N. prodotti' },
                          { var: '{stato}', desc: 'Stato preventivo' },
                          { var: '{nome_azienda}', desc: 'Nome azienda' },
                        ].map(v => (
                          <code key={v.var} className="bg-white px-2 py-0.5 rounded text-[11px] border border-blue-200 text-blue-800" title={v.desc}>
                            {v.var}
                          </code>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Templates list */}
                {(waTemplates || []).length === 0 ? (
                  <p className="text-sm text-[#4A5568] text-center py-8">{t('settings.waTemplates.noTemplates')}</p>
                ) : (
                  <div className="space-y-3">
                    {(waTemplates || []).map((tmpl) => (
                      <TemplateCard
                        key={tmpl.id}
                        tmpl={tmpl}
                        onEdit={() => openEditTemplate(tmpl)}
                        onDelete={() => deleteTemplate(tmpl)}
                        onDuplicate={() => duplicateTemplate(tmpl)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email" className="mt-6">
          <div className="max-w-3xl space-y-6">
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardHeader className="border-b border-[#E2E8F0] pb-4">
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                  <EnvelopeSimple size={20} className="text-[#0033A0]" />
                    {t('settings.email.title')}
                </CardTitle>
                <p className="text-sm text-[#4A5568] mt-1">
                  {t('settings.email.subtitle')}
                </p>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* How emails leave */}
                {emailSettingsLoaded.ready ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-sm space-y-1" data-testid="email-status-ready">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={18} className="text-green-600 shrink-0" />
                      <span className="text-sm text-green-800 font-medium">
                        {emailSettingsLoaded.mode === 'own' ? 'Le email partono dal tuo dominio' : 'Le email sono attive: non devi configurare nulla'}
                      </span>
                    </div>
                    <p className="text-sm text-green-900">Mittente visto dal cliente: <strong>{emailSettingsLoaded.from}</strong></p>
                    {emailSettingsLoaded.reply_to && (
                      <p className="text-sm text-green-900">Le risposte dei clienti arrivano a: <strong>{emailSettingsLoaded.reply_to}</strong></p>
                    )}
                    <p className="text-xs text-green-800">L'indirizzo per le risposte è l'email aziendale nella scheda Azienda.</p>
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2" data-testid="email-status-off">
                    <Warning size={18} className="text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-900">
                      L'invio email non è ancora attivo su questo server. Contatta l'assistenza Infismart, oppure usa il tuo dominio nella sezione Avanzate qui sotto.
                    </p>
                  </div>
                )}

                {/* Sender name */}
                <div>
                  <Label htmlFor="sender_name">{t('settings.email.senderName')}</Label>
                  <Input
                    id="sender_name"
                    value={emailSettings.sender_name}
                    onChange={(e) => setEmailSettings({ ...emailSettings, sender_name: e.target.value })}
                    placeholder={user?.company_name || 'es. Serramenti Rossi S.r.l.'}
                    className="mt-1"
                    data-testid="sender-name-input"
                  />
                  <p className="text-xs text-[#4A5568] mt-1">Se lo lasci vuoto viene usato il nome dell'azienda.</p>
                </div>

                {/* Advanced: own domain */}
                <div className="border border-[#E2E8F0] rounded-sm">
                  <button
                    type="button"
                    onClick={() => setShowEmailAdvanced(!showEmailAdvanced)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-[#0A0F1A]"
                    data-testid="email-advanced-toggle"
                  >
                    <span>Avanzate: invia dal tuo dominio (facoltativo)</span>
                    {showEmailAdvanced ? <CaretDown size={16} /> : <CaretRight size={16} />}
                  </button>
                  {showEmailAdvanced && (
                    <div className="px-3 pb-4 space-y-4 border-t border-[#E2E8F0] pt-3">
                      <p className="text-xs text-[#4A5568]">
                        Serve solo se vuoi che il mittente sia un indirizzo del tuo dominio (es. preventivi@tuaazienda.it).
                        Richiede un account Resend e la verifica del dominio nei DNS.
                      </p>
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="use_own_domain" className="text-sm">Usa il tuo dominio</Label>
                        <Switch
                          id="use_own_domain"
                          checked={emailSettings.use_own_domain}
                          onCheckedChange={(v) => setEmailSettings({ ...emailSettings, use_own_domain: v })}
                          data-testid="use-own-domain-switch"
                        />
                      </div>
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                        <div className="flex items-start gap-2">
                          <Info size={16} className="text-blue-600 mt-0.5 shrink-0" />
                          <div className="text-xs text-blue-700">
                            <p className="font-medium text-blue-800">{t('settings.email.howToGetKey')}</p>
                            <ol className="mt-1 space-y-1 list-decimal list-inside">
                              <li>{t('settings.email.step1')}</li>
                              <li>{t('settings.email.step2')}</li>
                              <li>{t('settings.email.step3')}</li>
                            </ol>
                          </div>
                        </div>
                      </div>
                      {emailSettingsLoaded.resend_api_key_set && (
                        <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-sm">
                          <CheckCircle size={18} className="text-green-600" />
                          <span className="text-sm text-green-800 font-medium">{t('settings.email.apiKeyConfigured')}</span>
                          <code className="text-xs bg-green-100 px-2 py-0.5 rounded ml-2">{emailSettingsLoaded.resend_api_key_masked}</code>
                        </div>
                      )}
                      <div>
                        <Label htmlFor="resend_api_key">{emailSettingsLoaded.resend_api_key_set ? t('settings.email.updateApiKey') : t('settings.email.apiKeyLabel')}</Label>
                        <div className="relative mt-1">
                          <Input
                            id="resend_api_key"
                            type={showApiKey ? 'text' : 'password'}
                            value={emailSettings.resend_api_key}
                            onChange={(e) => setEmailSettings({ ...emailSettings, resend_api_key: e.target.value })}
                            placeholder={emailSettingsLoaded.resend_api_key_set ? t('settings.email.keepCurrent') : t('settings.email.apiKeyPlaceholder')}
                            className="pr-10"
                            data-testid="resend-api-key-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKey(!showApiKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#0A0F1A]"
                          >
                            {showApiKey ? <EyeSlash size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="sender_email">{t('settings.email.senderEmail')}</Label>
                        <Input
                          id="sender_email"
                          type="email"
                          value={emailSettings.sender_email}
                          onChange={(e) => setEmailSettings({ ...emailSettings, sender_email: e.target.value })}
                          placeholder="es. preventivi@tuaazienda.it"
                          className="mt-1"
                          data-testid="sender-email-input"
                        />
                        <p className="text-xs text-[#4A5568] mt-1">
                          {t('settings.email.senderEmailHelp')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#E2E8F0]">
                  <Button
                    onClick={saveEmailSettings}
                    disabled={savingEmail}
                    className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                    data-testid="save-email-settings-btn"
                  >
                    {savingEmail ? <CircleNotch size={16} className="animate-spin mr-1" /> : <Check size={18} className="mr-1" />}
                    Salva Impostazioni
                  </Button>
                  <Button
                    variant="outline"
                    onClick={testEmailConnection}
                    disabled={testingEmail || !emailSettingsLoaded.ready}
                    className="border-[#0033A0] text-[#0033A0]"
                    data-testid="test-email-btn"
                  >
                    {testingEmail ? <CircleNotch size={16} className="animate-spin mr-1" /> : <PaperPlaneTilt size={16} className="mr-1" />}
                    {t('settings.email.sendTestEmail')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <ClientFollowupsSettings />

            {/* Email capabilities info */}
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText size={18} className="text-[#0033A0]" />
                  {t('settings.email.features')}
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { title: t('settings.email.feat1Title'), desc: t('settings.email.feat1Desc') },
                    { title: t('settings.email.feat2Title'), desc: t('settings.email.feat2Desc') },
                    { title: t('settings.email.feat3Title'), desc: t('settings.email.feat3Desc') },
                    { title: t('settings.email.feat4Title'), desc: t('settings.email.feat4Desc') },
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 bg-[#F4F6F8] rounded-sm">
                      <CheckCircle size={16} className="text-[#0033A0] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[#0A0F1A]">{f.title}</p>
                        <p className="text-xs text-[#4A5568]">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Quote Template Tab */}
        <TabsContent value="quote-template" className="mt-6">
          <QuoteTemplateWithPreview
            quoteTemplate={quoteTemplate}
            setQuoteTemplate={setQuoteTemplate}
            saveQuoteTemplate={saveQuoteTemplate}
            savingTemplate2={savingTemplate2}
            t={t}
          />
        </TabsContent>

        {/* SDI / Fatturazione Elettronica Tab (accounting module, not launched yet) */}
        {ACCOUNTING_LAUNCHED && (
        <TabsContent value="sdi" className="mt-6">
          <div className="max-w-3xl space-y-6">
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-outfit text-lg flex items-center gap-2">
                  <PaperPlaneTilt size={22} className="text-[#0033A0]" weight="duotone" />
                  Fatturazione Elettronica (OpenAPI.it)
                </CardTitle>
                <p className="text-sm text-[#4A5568] mt-1">
                  Configura l'integrazione con OpenAPI.it per l'invio automatico delle fatture al Sistema di Interscambio (SDI).
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Step 1: OAuth Token */}
                <div className="p-4 bg-[#F4F6F8] rounded-lg space-y-3">
                  <h4 className="font-bold text-sm text-[#0033A0]">1. Token OAuth OpenAPI.it</h4>
                  <p className="text-xs text-[#4A5568]">
                    Ottieni il token dalla console OpenAPI.it: <a href="https://console.openapi.com" target="_blank" rel="noopener noreferrer" className="text-[#0033A0] underline">console.openapi.com</a>
                  </p>
                  <div>
                    <Label className="text-xs">Token OAuth</Label>
                    <Input
                      type="password"
                      value={sdiConfig.oauth_token}
                      onChange={e => setSdiConfig({ ...sdiConfig, oauth_token: e.target.value })}
                      className="mt-1"
                      placeholder="Inserisci il token OAuth..."
                      data-testid="sdi-oauth-token"
                    />
                  </div>
                </div>

                {/* Step 2: Company Data */}
                <div className="p-4 bg-[#F4F6F8] rounded-lg space-y-3">
                  <h4 className="font-bold text-sm text-[#0033A0]">2. Dati Aziendali</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">P.IVA (Codice Fiscale Azienda)</Label>
                      <Input
                        value={sdiConfig.fiscal_id}
                        onChange={e => setSdiConfig({ ...sdiConfig, fiscal_id: e.target.value })}
                        className="mt-1"
                        placeholder="es. 12345678901"
                        data-testid="sdi-fiscal-id"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ragione Sociale</Label>
                      <Input
                        value={sdiConfig.company_name}
                        onChange={e => setSdiConfig({ ...sdiConfig, company_name: e.target.value })}
                        className="mt-1"
                        data-testid="sdi-company-name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Indirizzo</Label>
                      <Input
                        value={sdiConfig.company_address}
                        onChange={e => setSdiConfig({ ...sdiConfig, company_address: e.target.value })}
                        className="mt-1"
                        data-testid="sdi-company-address"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">CAP</Label>
                      <Input
                        value={sdiConfig.company_cap}
                        onChange={e => setSdiConfig({ ...sdiConfig, company_cap: e.target.value })}
                        className="mt-1"
                        placeholder="00100"
                        data-testid="sdi-company-cap"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Comune</Label>
                      <Input
                        value={sdiConfig.company_city}
                        onChange={e => setSdiConfig({ ...sdiConfig, company_city: e.target.value })}
                        className="mt-1"
                        data-testid="sdi-company-city"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Provincia (sigla)</Label>
                      <Input
                        value={sdiConfig.company_province}
                        onChange={e => setSdiConfig({ ...sdiConfig, company_province: e.target.value })}
                        className="mt-1"
                        placeholder="MI"
                        maxLength={2}
                        data-testid="sdi-company-province"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">PEC Aziendale</Label>
                      <Input
                        value={sdiConfig.company_pec}
                        onChange={e => setSdiConfig({ ...sdiConfig, company_pec: e.target.value })}
                        className="mt-1"
                        placeholder="azienda@pec.it"
                        data-testid="sdi-company-pec"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Regime Fiscale</Label>
                    <Select value={sdiConfig.regime_fiscale} onValueChange={v => setSdiConfig({ ...sdiConfig, regime_fiscale: v })}>
                      <SelectTrigger className="mt-1" data-testid="sdi-regime">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RF01">RF01 - Ordinario</SelectItem>
                        <SelectItem value="RF02">RF02 - Contribuenti minimi</SelectItem>
                        <SelectItem value="RF04">RF04 - Agricoltura</SelectItem>
                        <SelectItem value="RF19">RF19 - Forfettario</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Codice Destinatario Info */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium">
                    Codice Destinatario SDI: <span className="font-mono font-bold">JKKZDGR</span>
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Comunica questo codice ai tuoi clienti per ricevere le fatture passive tramite OpenAPI.it
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={saveSDISettings}
                    disabled={savingSDI}
                    className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                    data-testid="sdi-save"
                  >
                    {savingSDI ? <CircleNotch size={16} className="animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" />}
                    Salva Configurazione
                  </Button>
                  {sdiConfig.oauth_token && sdiConfig.fiscal_id && !sdiConfig.business_registered && (
                    <Button
                      onClick={handleRegisterBusiness}
                      disabled={registeringBusiness}
                      variant="outline"
                      className="border-green-500 text-green-700 hover:bg-green-50"
                      data-testid="sdi-register"
                    >
                      {registeringBusiness ? <CircleNotch size={16} className="animate-spin mr-2" /> : <PaperPlaneTilt size={16} className="mr-2" />}
                      Registra Azienda su OpenAPI.it
                    </Button>
                  )}
                </div>

                {/* Status */}
                {sdiConfig.business_registered && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-2">
                    <CheckCircle size={20} className="text-green-600" weight="fill" />
                    <span className="text-sm text-green-800 font-medium">Azienda registrata su OpenAPI.it SDI</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        )}

        {/* WhatsApp Notifications Tab (official WhatsApp API, not set up yet) */}
        {WHATSAPP_ALERTS_LAUNCHED && (
        <TabsContent value="wa-notifications" className="mt-6">
          <div className="max-w-3xl space-y-6">
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardHeader className="border-b border-[#E2E8F0] pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
                    <WhatsappLogo size={22} className="text-green-600" />
                    Notifiche WhatsApp
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="wa-enabled" className="text-sm text-[#4A5568]">Attivo</Label>
                    <input
                      id="wa-enabled"
                      type="checkbox"
                      checked={!!waNotifConfig.enabled && waNotifConfig.available !== false}
                      disabled={waNotifConfig.available === false}
                      onChange={(e) => setWaNotifConfig({ ...waNotifConfig, enabled: e.target.checked })}
                      className="h-5 w-5 accent-green-600 cursor-pointer"
                      data-testid="wa-notifications-enabled"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {waNotifConfig.available === false && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm flex items-start gap-2" data-testid="wa-notifications-coming-soon">
                    <Info size={16} className="text-blue-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-blue-900">
                      <p className="font-medium">In arrivo</p>
                      <p className="text-xs text-blue-800 mt-0.5">
                        Gli avvisi automatici su WhatsApp arriveranno con l'API ufficiale di WhatsApp. Nel frattempo gli stessi avvisi
                        ({ACCOUNTING_LAUNCHED ? 'fatture scadute, ' : ''}preventivi senza risposta, promemoria) ti arrivano come notifiche push: attivale nella scheda Notifiche Push.
                      </p>
                    </div>
                  </div>
                )}
                <div className={waNotifConfig.available === false ? 'space-y-6 opacity-50 pointer-events-none select-none' : 'space-y-6'} aria-disabled={waNotifConfig.available === false}>
                <div>
                  <Label className="text-sm font-medium">Il tuo numero WhatsApp</Label>
                  <p className="text-xs text-[#4A5568] mb-2">Formato internazionale (es. +39 331 1234567)</p>
                  <Input
                    type="tel"
                    placeholder="+39 331 1234567"
                    value={waNotifConfig.phone_number}
                    onChange={(e) => setWaNotifConfig({ ...waNotifConfig, phone_number: e.target.value })}
                    className="max-w-xs"
                    data-testid="wa-phone-number"
                  />
                </div>

                <div className="border-t border-[#E2E8F0] pt-4">
                  <Label className="text-sm font-semibold text-[#0A0F1A] mb-3 block">Tipo di notifiche</Label>
                  <div className="space-y-3">
                    {ACCOUNTING_LAUNCHED && (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={waNotifConfig.notify_overdue_invoices}
                        onChange={(e) => setWaNotifConfig({ ...waNotifConfig, notify_overdue_invoices: e.target.checked })}
                        className="h-4 w-4 accent-[#0033A0]"
                        data-testid="wa-notify-invoices"
                      />
                      <div>
                        <p className="text-sm font-medium">Fatture scadute</p>
                        <p className="text-xs text-[#4A5568]">Notifica giornaliera per fatture non pagate oltre la scadenza</p>
                      </div>
                    </label>
                    )}
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={waNotifConfig.notify_quote_followup}
                        onChange={(e) => setWaNotifConfig({ ...waNotifConfig, notify_quote_followup: e.target.checked })}
                        className="h-4 w-4 accent-[#0033A0]"
                        data-testid="wa-notify-quotes"
                      />
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="text-sm font-medium">Sollecito preventivi</p>
                          <p className="text-xs text-[#4A5568]">Avviso quando un preventivo inviato non riceve risposta</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-[#4A5568]">dopo</span>
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={waNotifConfig.followup_days}
                            onChange={(e) => setWaNotifConfig({ ...waNotifConfig, followup_days: parseInt(e.target.value) || 3 })}
                            className="w-14 h-7 text-xs text-center"
                            data-testid="wa-followup-days"
                          />
                          <span className="text-xs text-[#4A5568]">giorni</span>
                        </div>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={waNotifConfig.notify_reminders}
                        onChange={(e) => setWaNotifConfig({ ...waNotifConfig, notify_reminders: e.target.checked })}
                        className="h-4 w-4 accent-[#0033A0]"
                        data-testid="wa-notify-reminders"
                      />
                      <div>
                        <p className="text-sm font-medium">Promemoria scaduti</p>
                        <p className="text-xs text-[#4A5568]">Notifica quando un promemoria manuale supera la scadenza</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-4 border-t border-[#E2E8F0]">
                  <Button
                    onClick={saveWaNotifSettings}
                    disabled={savingWaNotif}
                    className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                    data-testid="save-wa-settings"
                  >
                    {savingWaNotif ? 'Salvataggio...' : 'Salva impostazioni'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={testWhatsApp}
                    disabled={testingWa || !waNotifConfig.phone_number}
                    className="border-green-600 text-green-600 hover:bg-green-50"
                    data-testid="test-wa-btn"
                  >
                    <WhatsappLogo size={16} className="mr-2" />
                    {testingWa ? 'Invio...' : 'Invia messaggio test'}
                  </Button>
                </div>

                <div className="bg-[#F4F6F8] rounded-sm p-4 text-xs text-[#4A5568] space-y-1">
                  <p className="font-semibold text-[#0A0F1A]">Come funziona</p>
                  <p>Il sistema controlla automaticamente ogni 2 minuti se ci sono {ACCOUNTING_LAUNCHED ? 'fatture scadute, ' : ''}preventivi senza risposta o promemoria scaduti, e ti invia una notifica WhatsApp.</p>
                  <p className="mt-2 font-semibold text-[#0A0F1A]">Configurazione iniziale Sandbox</p>
                  <p>Invia il messaggio "join" indicato nella console Twilio al numero +1 415 523 8886 dal tuo WhatsApp per attivare la sandbox.</p>
                </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        )}

        {/* Push Notifications Tab */}
        <TabsContent value="push-notifications" className="mt-6">
          <PushNotificationsSettings />
        </TabsContent>

        {user?.is_platform_admin && (
          <TabsContent value="admin-users" className="mt-6">
            <AdminUsersTab />
          </TabsContent>
        )}

        <TabsContent value="subscription" className="mt-6">
          <SubscriptionSettings />
        </TabsContent>
      </Tabs>

      {/* Add Custom Profile Dialog */}
      <Dialog open={isAddProfileOpen} onOpenChange={setIsAddProfileOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Nuovo Profilo Personalizzato</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome Profilo</Label>
              <Input
                value={newProfile.name}
                onChange={(e) => setNewProfile({ ...newProfile, name: e.target.value })}
                placeholder="es. PVC 6 Camere"
                className="mt-1"
                data-testid="new-profile-name-input"
              />
            </div>
            <div>
              <Label>Prezzi</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[{ id: 'own', label: 'Prezzi propri', hint: 'li scrivi nel Listino prezzi' },
                  { id: 'markup', label: '% su un profilo base', hint: 'calcolati in automatico' }].map(o => (
                  <button key={o.id} type="button" onClick={() => setNewProfile({ ...newProfile, pricing: o.id })}
                    className={`text-left rounded-sm border px-3 py-2 ${newProfile.pricing === o.id ? 'border-[#0033A0] bg-[#0033A0]/5' : 'border-[#E2E8F0]'}`}
                    data-testid={`new-profile-pricing-${o.id}`}>
                    <span className="block text-sm font-medium">{o.label}</span>
                    <span className="block text-[11px] text-[#4A5568]">{o.hint}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>{newProfile.pricing === 'own' ? 'Materiale di riferimento' : 'Profilo Base (Categoria)'}</Label>
              <Select
                value={newProfile.parent_profile_id}
                onValueChange={(v) => setNewProfile({ ...newProfile, parent_profile_id: v })}
              >
                <SelectTrigger className="mt-1" data-testid="new-profile-parent-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {profileTypes.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#4A5568] mt-1">
                {newProfile.pricing === 'own'
                  ? 'Il profilo compare nel listino accanto a questo materiale; dove non metti un prezzo si usa il suo'
                  : 'I prezzi saranno calcolati a partire da questo profilo base'}
              </p>
            </div>
            {newProfile.pricing !== 'own' && (
            <div>
              <Label>Maggiorazione (%)</Label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  step="0.1"
                  value={newProfile.percentage_markup}
                  onChange={(e) => setNewProfile({ ...newProfile, percentage_markup: parseFloat(e.target.value) || 0 })}
                  className="pr-8"
                  data-testid="new-profile-markup-input"
                />
                <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
              </div>
              <p className="text-xs text-[#4A5568] mt-1">
                Es: 10% = il prezzo sarà 10% in più rispetto al profilo base
              </p>
            </div>
            )}
            <div>
              <Label>Descrizione Profilo</Label>
              <Textarea
                value={newProfile.description}
                onChange={(e) => setNewProfile({ ...newProfile, description: e.target.value })}
                placeholder="es. Profilo PVC a 6 camere con rinforzo in acciaio, Uf=1.0 W/m²K"
                className="mt-1"
                rows={2}
                data-testid="new-profile-description-input"
              />
              <p className="text-xs text-[#4A5568] mt-1">
                Questa descrizione comparirà nella stampa del preventivo per ogni prodotto
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsAddProfileOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleAddProfile}
                className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                data-testid="confirm-add-profile-button"
              >
                <Plus size={18} className="mr-1" />
                Aggiungi Profilo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Template Dialog */}
      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <WhatsappLogo size={22} className="text-[#25D366]" />
              {editingTemplate ? t('settings.waTemplates.editTemplate') : t('settings.waTemplates.newTemplate')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('settings.waTemplates.templateName')}</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="es. Conferma Ordine"
                className="mt-1"
                data-testid="template-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('settings.waTemplates.category')}</Label>
                <Select value={templateForm.category} onValueChange={(v) => setTemplateForm({ ...templateForm, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quote">{t('settings.waTemplates.categories.quote')}</SelectItem>
                    <SelectItem value="followup">{t('settings.waTemplates.categories.followup')}</SelectItem>
                    <SelectItem value="appointment">{t('settings.waTemplates.categories.appointment')}</SelectItem>
                    <SelectItem value="thanks">{t('settings.waTemplates.categories.thanks')}</SelectItem>
                    <SelectItem value="order">{t('settings.waTemplates.categories.order')}</SelectItem>
                    <SelectItem value="delivery">{t('settings.waTemplates.categories.delivery')}</SelectItem>
                    <SelectItem value="custom">{t('settings.waTemplates.categories.custom')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('settings.waTemplates.icon')}</Label>
                <Select value={templateForm.icon} onValueChange={(v) => setTemplateForm({ ...templateForm, icon: v })}>
                  <SelectTrigger className="mt-1" data-testid="template-icon-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FileText">{t('settings.waTemplates.icons.document')}</SelectItem>
                    <SelectItem value="ChatCircleDots">{t('settings.waTemplates.icons.chat')}</SelectItem>
                    <SelectItem value="CalendarCheck">{t('settings.waTemplates.icons.calendar')}</SelectItem>
                    <SelectItem value="Megaphone">{t('settings.waTemplates.icons.megaphone')}</SelectItem>
                    <SelectItem value="Truck">{t('settings.waTemplates.icons.shipping')}</SelectItem>
                    <SelectItem value="Check">{t('settings.waTemplates.icons.confirm')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('settings.waTemplates.messageBody')}</Label>
              <Textarea
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                placeholder="Gentile {nome_cliente},&#10;&#10;..."
                className="mt-1 font-mono text-sm"
                rows={8}
                data-testid="template-body-input"
              />
              <p className="text-xs text-[#4A5568] mt-1">
                {t('settings.waTemplates.variablesLabel')}: <code className="bg-gray-100 px-1 rounded">{'{nome_cliente}'}</code> <code className="bg-gray-100 px-1 rounded">{'{numero_preventivo}'}</code> <code className="bg-gray-100 px-1 rounded">{'{totale}'}</code> <code className="bg-gray-100 px-1 rounded">{'{num_prodotti}'}</code> <code className="bg-gray-100 px-1 rounded">{'{stato}'}</code> <code className="bg-gray-100 px-1 rounded">{'{nome_azienda}'}</code>
              </p>
            </div>

            {/* Preview */}
            {templateForm.body && (
              <div>
                <Label className="text-xs text-[#4A5568]">{t('settings.waTemplates.preview')}</Label>
                <div className="mt-1 p-3 bg-[#DCF8C6] rounded-lg text-sm whitespace-pre-wrap border border-[#25D366]/20">
                  {templateForm.body
                    .replace(/\{nome_cliente\}/g, 'Mario Rossi')
                    .replace(/\{numero_preventivo\}/g, 'P-001')
                    .replace(/\{totale\}/g, '\u20AC1.250,00')
                    .replace(/\{num_prodotti\}/g, '3')
                    .replace(/\{stato\}/g, 'In attesa')
                    .replace(/\{nome_azienda\}/g, user?.company_name || 'Serramenti S.r.l.')}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsTemplateDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={saveTemplate}
                disabled={savingTemplate}
                className="bg-[#25D366] hover:bg-[#1DA851] text-white"
                data-testid="save-template-btn"
              >
                {savingTemplate ? t('common.saving') : (
                  <>
                    <Check size={18} className="mr-1" />
                    {editingTemplate ? t('settings.waTemplates.saveChanges') : t('settings.waTemplates.createTemplate')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const CATEGORY_LABELS = {
  quote: 'Preventivo',
  followup: 'Sollecito',
  appointment: 'Appuntamento',
  thanks: 'Ringraziamento',
  order: 'Ordine',
  delivery: 'Consegna',
  custom: 'Personalizzato'
};

const CATEGORY_COLORS = {
  quote: 'bg-blue-100 text-blue-700',
  followup: 'bg-amber-100 text-amber-700',
  appointment: 'bg-purple-100 text-purple-700',
  thanks: 'bg-green-100 text-green-700',
  order: 'bg-indigo-100 text-indigo-700',
  delivery: 'bg-cyan-100 text-cyan-700',
  custom: 'bg-gray-100 text-gray-700'
};

const ICON_MAP = {
  FileText, ChatCircleDots, CalendarCheck, Megaphone, Truck, Check
};

function TemplateCard({ tmpl, onEdit, onDelete, onDuplicate }) {
  const { t } = useTranslation();
  const IconComp = ICON_MAP[tmpl.icon] || ChatCircleDots;
  const categoryLabel = t(`settings.waTemplates.categories.${tmpl.category}`) || tmpl.category;
  return (
    <div className="border border-[#E2E8F0] rounded-sm p-4 hover:border-[#25D366]/40 transition-colors" data-testid={`template-card-${tmpl.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-sm bg-[#25D366]/10 flex items-center justify-center shrink-0">
            <IconComp size={18} className="text-[#25D366]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-[#0A0F1A]">{tmpl.name}</h4>
              <Badge className={`text-[10px] border-0 ${CATEGORY_COLORS[tmpl.category] || CATEGORY_COLORS.custom}`}>
                {categoryLabel}
              </Badge>
              {tmpl.is_default && (
                <Badge variant="outline" className="text-[10px] text-[#4A5568]">{t('settings.waTemplates.default')}</Badge>
              )}
            </div>
            <p className="text-xs text-[#4A5568] line-clamp-2 whitespace-pre-wrap">{tmpl.body}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} className="p-1.5 rounded hover:bg-[#F4F6F8] text-[#4A5568] hover:text-[#0033A0] transition-colors" title="Modifica" data-testid={`edit-template-${tmpl.id}`}>
            <PencilSimple size={16} />
          </button>
          <button onClick={onDuplicate} className="p-1.5 rounded hover:bg-[#F4F6F8] text-[#4A5568] hover:text-[#0033A0] transition-colors" title="Duplica" data-testid={`duplicate-template-${tmpl.id}`}>
            <Copy size={16} />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-50 text-[#4A5568] hover:text-red-600 transition-colors" title="Elimina" data-testid={`delete-template-${tmpl.id}`}>
            <Trash size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}



function SubscriptionSettings() {
  const { plan, planName, inTrial, trialDaysLeft, canAccess } = useSubscription();
  const { updateUser } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [subData, setSubData] = React.useState(null);
  const [downgradeTarget, setDowngradeTarget] = React.useState(null);

  React.useEffect(() => {
    const fetchSub = async () => {
      try {
        const { data } = await axios.get(`${API}/subscription/current`);
        setSubData(data || {})
      } catch (err) {
        console.error('Error fetching subscription:', err);
      }
    };
    fetchSub();
  }, []);

  const changePlan = async (newPlan) => {
    if (newPlan === plan) return;
    setLoading(true);
    try {
      await axios.put(`${API}/subscription/plan`, { plan: newPlan });
      updateUser({ plan: newPlan });
      const { data } = await axios.get(`${API}/subscription/current`);
      setSubData(data || {})
      toast.success(`Piano aggiornato a ${data.plan_name}`);
      setDowngradeTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio piano');
    }
    setLoading(false);
  };

  const planOrder = ['preventivi_ai', 'gestionale', 'sartoriale'];
  const isDowngrade = (targetId) => planOrder.indexOf(targetId) < planOrder.indexOf(plan);

  const handlePlanClick = (targetId) => {
    if (targetId === plan) return;
    if (isDowngrade(targetId)) {
      setDowngradeTarget(targetId);
    } else {
      changePlan(targetId);
    }
  };

  const plans = [
    {
      id: 'preventivi_ai',
      name: 'Preventivi & IA',
      price: 15,
      desc: 'Preventivi intelligenti, CRM e assistenza IA',
      features: [
        'Quote Builder con 156 icone prodotto',
        'Generazione PDF personalizzabili',
        'Invio preventivi e documenti via email',
        'Sopralluogo IA e suggerimenti',
        'Gestione clienti e CRM Opportunità',
        'Solleciti e promemoria',
        'Sistema ticket supporto',
        ...(ACCOUNTING_LAUNCHED ? [] : ['Notifiche Push e riepilogo mattutino', 'App Mobile PWA'])
      ]
    },
    {
      id: 'gestionale',
      name: 'Gestionale Completo',
      price: 49,
      badge: 'Il più scelto',
      // Shown before the launch only to companies already on this plan, without the accounting items
      desc: ACCOUNTING_LAUNCHED ? 'Contabilità completa, notifiche e app mobile' : 'Il tuo piano attuale',
      features: ACCOUNTING_LAUNCHED ? [
        'Tutto del piano Preventivi & IA',
        'Fatturazione elettronica SDI',
        'DDT, Proforma, Note di Credito',
        'Dashboard contabile completa',
        'Notifiche WhatsApp automatiche',
        'Notifiche Push e riepilogo mattutino',
        'App Mobile PWA'
      ] : ['Tutto del piano Preventivi & IA']
    },
    {
      id: 'sartoriale',
      name: 'Sartoriale',
      price: null,
      desc: 'Soluzioni su misura per la tua azienda',
      features: [
        ACCOUNTING_LAUNCHED ? 'Tutto del piano Gestionale Completo' : 'Tutto del piano Preventivi & IA',
        'Integrazioni ERP personalizzate',
        'Configurazioni multi-sede',
        'Account Manager dedicato',
        'Sviluppo funzionalità su richiesta'
      ]
    }
  ].filter(p => isPlanOffered(p.id) || p.id === plan);

  const trialProgress = inTrial ? Math.max(0, ((7 - trialDaysLeft) / 7) * 100) : 100;

  return (
    <div className="space-y-6">
      {/* Current Plan Status */}
      <Card className="border-[#E2E8F0]">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="current-plan-status">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${plan === 'gestionale' ? 'bg-[#0033A0]' : plan === 'sartoriale' ? 'bg-gradient-to-br from-[#0033A0] to-purple-600' : 'bg-[#0033A0]/10'}`}>
                <Crown size={24} weight="fill" className={plan === 'gestionale' || plan === 'sartoriale' ? 'text-white' : 'text-[#0033A0]'} />
              </div>
              <div>
                <h3 className="font-outfit text-lg font-semibold text-[#0A0F1A]">{planName}</h3>
                <p className="text-sm text-[#4A5568]">
                  {subData?.price ? `\u20AC${subData.price}/mese` : 'Piano personalizzato'}
                </p>
              </div>
            </div>
            {inTrial && (
              <div className="sm:text-right">
                <p className="text-sm font-medium text-amber-700">Prova Gratuita</p>
                <p className="text-xs text-amber-600">{trialDaysLeft} giorn{trialDaysLeft === 1 ? 'o' : 'i'} rimanent{trialDaysLeft === 1 ? 'e' : 'i'}</p>
              </div>
            )}
          </div>

          {/* Trial progress bar */}
          {inTrial && (
            <div className="mt-4" data-testid="trial-progress">
              <div className="flex items-center justify-between text-xs text-[#4A5568] mb-1.5">
                <span>Prova gratuita</span>
                <span>{trialDaysLeft} di 7 giorni</span>
              </div>
              <div className="w-full h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${trialProgress}%` }} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans Grid */}
      <div className={`grid grid-cols-1 ${plans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`} data-testid="plans-grid">
        {plans.map((p) => {
          const isActive = p.id === plan;
          return (
            <Card
              key={p.id}
              className={`relative transition-all ${isActive ? 'border-2 border-[#0033A0] shadow-md' : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:shadow-sm'}`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#F59E0B] text-[#0A0F1A] text-[10px] font-bold px-3 py-1 rounded-full shadow-sm whitespace-nowrap">
                    {p.badge}
                  </span>
                </div>
              )}
              {isActive && (
                <div className="absolute -top-3 right-4">
                  <span className="bg-[#0033A0] text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-sm">
                    Piano Attivo
                  </span>
                </div>
              )}
              <CardContent className="p-5 pt-6 flex flex-col h-full">
                <h4 className="font-outfit text-base font-semibold text-[#0A0F1A]">{p.name}</h4>
                <p className="text-xs text-[#4A5568] mt-1 mb-4">{p.desc}</p>

                <div className="mb-4">
                  {p.price !== null ? (
                    <div className="flex items-baseline gap-0.5">
                      <span className="font-outfit text-3xl font-bold text-[#0A0F1A]">{'\u20AC'}{p.price}</span>
                      <span className="text-sm text-[#4A5568]">/mese</span>
                    </div>
                  ) : (
                    <span className="font-outfit text-xl font-bold text-[#0A0F1A]">Su misura</span>
                  )}
                </div>

                <ul className="space-y-2 flex-1 mb-5">
                  {p.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2">
                      <CheckCircle size={14} weight="fill" className={`mt-0.5 flex-shrink-0 ${isActive ? 'text-[#0033A0]' : 'text-green-500'}`} />
                      <span className="text-xs text-[#4A5568]">{f}</span>
                    </li>
                  ))}
                </ul>

                {isActive ? (
                  <div className="w-full text-center py-2.5 rounded-lg bg-[#0033A0]/5 text-[#0033A0] font-semibold text-sm border border-[#0033A0]/20" data-testid={`plan-active-${p.id}`}>
                    Piano Attuale
                  </div>
                ) : (
                  <button
                    onClick={() => handlePlanClick(p.id)}
                    disabled={loading}
                    className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all ${
                      p.id === 'sartoriale'
                        ? 'border border-[#E2E8F0] text-[#0A0F1A] hover:border-[#0033A0] hover:text-[#0033A0]'
                        : 'bg-[#0033A0] text-white hover:bg-[#002880] shadow-sm'
                    }`}
                    data-testid={`plan-switch-${p.id}`}
                  >
                    {loading ? (
                      <CircleNotch size={16} className="animate-spin mx-auto" />
                    ) : p.id === 'sartoriale' ? 'Contattaci' : (
                      plans.findIndex(x => x.id === p.id) > plans.findIndex(x => x.id === plan)
                        ? 'Aggiorna Piano' : 'Cambia Piano'
                    )}
                  </button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info note */}
      <Card className="border-[#E2E8F0] bg-[#F4F6F8]">
        <CardContent className="p-4 flex items-start gap-3">
          <Info size={18} className="text-[#4A5568] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-[#4A5568]">
              Il cambio piano è immediato. Le funzionalità aggiuntive saranno disponibili subito dopo l'aggiornamento.
              {inTrial && ' Durante la prova gratuita hai accesso a tutte le funzionalità.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Downgrade Confirmation Dialog */}
      <Dialog open={!!downgradeTarget} onOpenChange={(open) => { if (!open) setDowngradeTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="downgrade-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2">
              <Lock size={20} className="text-amber-500" />
              Conferma Downgrade
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#4A5568]">
              Passando al piano <strong>{plans.find(p => p.id === downgradeTarget)?.name}</strong> perderai l'accesso a:
            </p>
            <ul className="space-y-2">
              {downgradeTarget === 'preventivi_ai' && (ACCOUNTING_LAUNCHED ? [
                'Fatturazione elettronica SDI',
                'DDT, Proforma, Note di Credito',
                'Dashboard contabile',
                'Notifiche WhatsApp automatiche',
                'Notifiche Push e riepilogo mattutino',
                'App Mobile PWA'
              ] : ['Integrazioni e configurazioni su misura']).map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-red-600">
                  <Lock size={12} className="flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                I tuoi dati rimarranno salvati. Potrai tornare al piano superiore in qualsiasi momento e ritrovare tutto.
              </p>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setDowngradeTarget(null)}
              className="flex-1 py-2.5 rounded-lg border border-[#E2E8F0] text-sm font-medium text-[#4A5568] hover:bg-[#F4F6F8] transition-colors"
              data-testid="downgrade-cancel"
            >
              Annulla
            </button>
            <button
              onClick={() => changePlan(downgradeTarget)}
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors"
              data-testid="downgrade-confirm"
            >
              {loading ? <CircleNotch size={16} className="animate-spin mx-auto" /> : 'Conferma Downgrade'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuoteTemplateWithPreview({ quoteTemplate, setQuoteTemplate, saveQuoteTemplate, savingTemplate2, t }) {
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const debounceRef = React.useRef(null);

  const loadPreview = React.useCallback(async (templateData) => {
    setPreviewLoading(true);
    try {
      await axios.put(`${API}/settings/quote-template`, { quote_template: templateData });
      const response = await axios.get(`${API}/settings/quote-template/preview`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(prev => {
        if (prev) window.URL.revokeObjectURL(prev);
        return url;
      });
    } catch (err) {
      console.error('Preview error:', err);
    }
    setPreviewLoading(false);
  }, []);

  // Auto-refresh preview on template change with debounce
  React.useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadPreview(quoteTemplate);
    }, 1500);

    return () => clearTimeout(debounceRef.current);
  }, [quoteTemplate, loadPreview]);

  // Cleanup blob on unmount
  React.useEffect(() => {
    return () => {
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      {/* Left: Settings */}
      <div className="space-y-6">
        <Card className="border border-[#E2E8F0] rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText size={18} className="text-[#0033A0]" />
              {t('settings.quoteTemplate.title')}
            </CardTitle>
            <p className="text-sm text-[#4A5568] mt-1">{t('settings.quoteTemplate.subtitle')}</p>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Primary Color & Font Size */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium">{t('settings.quoteTemplate.primaryColor')}</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="color"
                    value={quoteTemplate.primary_color}
                    onChange={e => setQuoteTemplate({ ...quoteTemplate, primary_color: e.target.value })}
                    className="w-10 h-10 rounded border border-[#E2E8F0] cursor-pointer"
                    data-testid="template-color-picker"
                  />
                  <Input
                    value={quoteTemplate.primary_color}
                    onChange={e => setQuoteTemplate({ ...quoteTemplate, primary_color: e.target.value })}
                    className="w-28 font-mono text-sm"
                    data-testid="template-color-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium">{t('settings.quoteTemplate.fontSize')}</Label>
                <Select value={quoteTemplate.font_size} onValueChange={v => setQuoteTemplate({ ...quoteTemplate, font_size: v })}>
                  <SelectTrigger className="mt-1" data-testid="template-font-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">{t('settings.quoteTemplate.fontSmall')}</SelectItem>
                    <SelectItem value="normal">{t('settings.quoteTemplate.fontNormal')}</SelectItem>
                    <SelectItem value="large">{t('settings.quoteTemplate.fontLarge')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Visibility Toggles */}
            <div>
              <Label className="text-xs font-medium mb-2 block">{t('settings.quoteTemplate.visibleSections')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'show_logo', label: 'Logo' },
                  { key: 'show_product_details', label: t('settings.quoteTemplate.productDetails') },
                  { key: 'show_product_icons', label: t('settings.quoteTemplate.productIcons') },
                  { key: 'show_iva_column', label: t('settings.quoteTemplate.ivaColumn') },
                  { key: 'show_terms', label: t('settings.quoteTemplate.terms') },
                  { key: 'show_signature', label: t('settings.quoteTemplate.signature') },
                  { key: 'show_notes', label: t('settings.quoteTemplate.notes') },
                  { key: 'show_validity', label: t('settings.quoteTemplate.validity') }
                ].map(({ key, label }) => (
                  <label
                    key={key}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${quoteTemplate[key] ? 'border-[#0033A0] bg-blue-50' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E0]'}`}
                    data-testid={`template-toggle-${key}`}
                  >
                    <input
                      type="checkbox"
                      checked={quoteTemplate[key]}
                      onChange={e => setQuoteTemplate({ ...quoteTemplate, [key]: e.target.checked })}
                      className="rounded border-[#CBD5E0] text-[#0033A0] focus:ring-[#0033A0]"
                    />
                    <span className="text-xs font-medium text-[#0A0F1A]">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Section Titles */}
            <div>
              <Label className="text-xs font-medium mb-2 block">{t('settings.quoteTemplate.sectionTitles')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'header_title', label: t('settings.quoteTemplate.headerTitle') },
                  { key: 'client_section_title', label: t('settings.quoteTemplate.clientSection') },
                  { key: 'products_section_title', label: t('settings.quoteTemplate.productsSection') },
                  { key: 'summary_section_title', label: t('settings.quoteTemplate.summarySection') },
                  { key: 'notes_section_title', label: t('settings.quoteTemplate.notesSection') },
                  { key: 'terms_section_title', label: t('settings.quoteTemplate.termsSection') }
                ].map(({ key, label }) => (
                  <div key={key}>
                    <Label className="text-[10px] text-[#4A5568]">{label}</Label>
                    <Input
                      value={quoteTemplate[key]}
                      onChange={e => setQuoteTemplate({ ...quoteTemplate, [key]: e.target.value })}
                      className="mt-0.5 text-sm h-8"
                      data-testid={`template-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Signature Labels */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-[#4A5568]">{t('settings.quoteTemplate.signatureLeft')}</Label>
                <Input
                  value={quoteTemplate.signature_left}
                  onChange={e => setQuoteTemplate({ ...quoteTemplate, signature_left: e.target.value })}
                  className="mt-0.5 text-sm h-8"
                  data-testid="template-signature-left"
                />
              </div>
              <div>
                <Label className="text-[10px] text-[#4A5568]">{t('settings.quoteTemplate.signatureRight')}</Label>
                <Input
                  value={quoteTemplate.signature_right}
                  onChange={e => setQuoteTemplate({ ...quoteTemplate, signature_right: e.target.value })}
                  className="mt-0.5 text-sm h-8"
                  data-testid="template-signature-right"
                />
              </div>
            </div>

            {/* Footer Text */}
            <div>
              <Label className="text-xs font-medium">{t('settings.quoteTemplate.footerText')}</Label>
              <Textarea
                value={quoteTemplate.footer_text}
                onChange={e => setQuoteTemplate({ ...quoteTemplate, footer_text: e.target.value })}
                className="mt-1 text-sm"
                rows={2}
                placeholder={t('settings.quoteTemplate.footerPlaceholder')}
                data-testid="template-footer-text"
              />
            </div>

            {/* Save Button */}
            <Button
              onClick={saveQuoteTemplate}
              disabled={savingTemplate2}
              className="w-full bg-[#0033A0] hover:bg-[#0047AB] text-white"
              data-testid="save-quote-template"
            >
              {savingTemplate2 ? (
                <><CircleNotch size={18} className="mr-2 animate-spin" /> {t('common.saving')}</>
              ) : (
                <><Check size={18} className="mr-2" /> {t('settings.quoteTemplate.save')}</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Right: PDF Preview */}
      <div className="xl:sticky xl:top-4 xl:self-start">
        <Card className="border border-[#E2E8F0] rounded-sm overflow-hidden" data-testid="template-preview-card">
          <CardHeader className="pb-2 border-b border-[#E2E8F0]">
            <CardTitle className="text-sm font-semibold text-[#0A0F1A] flex items-center gap-2">
              <Eye size={16} className="text-[#0033A0]" />
              Anteprima PDF
              {previewLoading && <CircleNotch size={14} className="animate-spin text-[#0033A0]" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {previewUrl ? (
              <iframe
                src={previewUrl}
                title="Anteprima Template"
                className="w-full border-0 h-[70vh]"
                data-testid="template-preview-iframe"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-[70vh] bg-[#F4F6F8]">
                {previewLoading ? (
                  <>
                    <CircleNotch size={24} className="animate-spin text-[#0033A0] mb-3" />
                    <p className="text-sm text-[#4A5568]">Generazione anteprima...</p>
                  </>
                ) : (
                  <p className="text-sm text-[#4A5568]">Caricamento anteprima PDF...</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PushNotificationsSettings() {
  const { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe } = usePushNotifications();
  const [preferences, setPreferences] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  React.useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const { data } = await axios.get(`${API}/push/preferences`);
        setPreferences(data || {})
      } catch (err) {
        console.error('Error fetching push preferences:', err);
      }
    };
    fetchPrefs();
  }, []);

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
      toast.success('Notifiche push disattivate');
    } else {
      const ok = await subscribe();
      if (ok) {
        toast.success('Notifiche push attivate!');
      } else if (permission === 'denied') {
        toast.error('Permesso notifiche negato. Abilita le notifiche nelle impostazioni del browser.');
      }
    }
  };

  const savePrefs = async (updated) => {
    setSaving(true);
    try {
      await axios.put(`${API}/push/preferences`, updated);
      setPreferences(updated);
      toast.success('Preferenze salvate');
    } catch (err) {
      toast.error('Errore nel salvataggio');
    }
    setSaving(false);
  };

  const updatePref = (key, value) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    savePrefs(updated);
  };

  const testPush = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/push/test`);
      toast.success(data.message || 'Notifica test inviata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel test');
    }
    setTesting(false);
  };

  if (!isSupported) {
    return (
      <Card className="border-[#E2E8F0]">
        <CardContent className="p-6 text-center">
          <Bell size={32} className="mx-auto text-[#4A5568] mb-3" />
          <p className="text-[#4A5568]">
            Il tuo browser non supporta le notifiche push. Prova con Chrome, Firefox o Edge.
          </p>
        </CardContent>
      </Card>
    );
  }

  const notifTypes = [
    { key: 'notify_daily_summary', label: 'Riepilogo mattutino', desc: 'Ogni mattina alle 8:00 un riepilogo delle attivita del giorno' },
    { key: 'notify_overdue_invoices', label: 'Fatture scadute', desc: 'Ricevi una notifica quando una fattura supera la data di scadenza' },
    { key: 'notify_expiring_quotes', label: 'Preventivi in scadenza', desc: 'Notifica quando un preventivo sta per scadere' },
    { key: 'notify_followups', label: 'Solleciti da fare', desc: 'Promemoria per solleciti con i clienti' },
    { key: 'notify_reminders', label: 'Promemoria', desc: 'Notifiche per promemoria scaduti' },
    { key: 'notify_passive_invoices', label: 'Fatture passive', desc: 'Avviso quando ricevi una nuova fattura passiva via SDI' },
  ].filter(n => ACCOUNTING_LAUNCHED || !['notify_overdue_invoices', 'notify_passive_invoices'].includes(n.key));

  return (
    <div className="space-y-6">
      {/* Main Toggle */}
      <Card className="border-[#E2E8F0]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between" data-testid="push-toggle-section">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSubscribed ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Bell size={22} className={isSubscribed ? 'text-green-600' : 'text-gray-400'} weight={isSubscribed ? 'fill' : 'regular'} />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A0F1A]">Notifiche Push</h3>
                <p className="text-xs text-[#4A5568]">
                  {isSubscribed ? 'Attive - Riceverai notifiche anche a browser chiuso' : 'Disattive - Attiva per ricevere avvisi in tempo reale'}
                </p>
              </div>
            </div>
            <Button
              onClick={handleToggle}
              disabled={loading}
              className={isSubscribed ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-[#0033A0] hover:bg-[#0047AB] text-white'}
              data-testid="push-toggle-btn"
            >
              {loading ? (
                <CircleNotch size={16} className="animate-spin" />
              ) : isSubscribed ? 'Disattiva' : 'Attiva'}
            </Button>
          </div>
          {permission === 'denied' && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              Le notifiche sono bloccate dal browser. Vai nelle impostazioni del sito per abilitarle.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Button */}
      {isSubscribed && (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm text-[#0A0F1A]">Test Notifica</p>
              <p className="text-xs text-[#4A5568]">Invia una notifica di prova per verificare il funzionamento</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={testPush}
              disabled={testing}
              className="border-[#0033A0] text-[#0033A0]"
              data-testid="push-test-btn"
            >
              {testing ? <CircleNotch size={14} className="animate-spin mr-1" /> : <Bell size={14} className="mr-1" />}
              Invia Test
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Notification Preferences */}
      {isSubscribed && preferences && (
        <Card className="border-[#E2E8F0]">
          <CardHeader className="border-b border-[#E2E8F0] pb-4">
            <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A]">
              Tipi di Notifica
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-[#E2E8F0]">
            {notifTypes.map(({ key, label, desc }) => (
              <div key={key} className="p-4 flex items-center justify-between" data-testid={`push-pref-${key}`}>
                <div className="flex-1 mr-4">
                  <p className="text-sm font-medium text-[#0A0F1A]">{label}</p>
                  <p className="text-xs text-[#4A5568]">{desc}</p>
                </div>
                <button
                  onClick={() => updatePref(key, !preferences[key])}
                  className={`relative w-11 h-6 rounded-full transition-colors ${preferences[key] !== false ? 'bg-[#0033A0]' : 'bg-gray-300'}`}
                  data-testid={`push-toggle-${key}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${preferences[key] !== false ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            ))}

            {/* Days before expiry */}
            {preferences.notify_expiring_quotes !== false && (
              <div className="p-4 flex items-center justify-between" data-testid="push-expiring-days">
                <div className="flex-1 mr-4">
                  <p className="text-sm font-medium text-[#0A0F1A]">Giorni anticipo scadenza</p>
                  <p className="text-xs text-[#4A5568]">Quanti giorni prima della scadenza vuoi essere notificato</p>
                </div>
                <select
                  value={preferences.expiring_days_before || 2}
                  onChange={(e) => updatePref('expiring_days_before', parseInt(e.target.value))}
                  className="h-9 px-3 border border-[#E2E8F0] rounded-md text-sm bg-white"
                >
                  <option value="1">1 giorno</option>
                  <option value="2">2 giorni</option>
                  <option value="3">3 giorni</option>
                  <option value="5">5 giorni</option>
                  <option value="7">7 giorni</option>
                </select>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


